import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import {
  collection, doc, addDoc, updateDoc, setDoc, getDoc, getDocs,
  query, where, orderBy, serverTimestamp, arrayUnion, increment, runTransaction, writeBatch,
} from "firebase/firestore";
import { WaterTankerExpensesTab } from "./WaterScreen";
import BankScreen from "./BankScreen";

// ════════════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════════════
function currentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

// Moved in from gmailService.js — used by assignUnmatchedDebit / correctExpenseClassification
function monthKeyFromDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Restored from gmailService.js — when a bank credit is manually matched to a
// flat, this remembers the payer's details on the FLAT OWNER's own record
// (not the flat's record), because that is what bank-sync.js actually checks
// when it tries to auto-match future payments. Saves:
//   - the UPI ID (if the payment had one), and/or
//   - the bank-sent name (for IMPS/NEFT transfers with no UPI ID) —
//     added to a list, so more than one family member's name can be
//     remembered for the same flat.
// Also adds the owner's own name, legal name, and flat number as a few
// free "known names" so future transfers may match even on the first try.
async function learnPayerForFlat(flatId, credit) {
  const ownerSnap = await getDocs(query(
    collection(db, "users"),
    where("flatId", "==", flatId),
    where("memberType", "==", "owner")
  ));
  if (ownerSnap.empty) return; // no owner record found — nothing to save to

  const ownerDoc   = ownerSnap.docs[0];
  const ownerRef   = doc(db, "users", ownerDoc.id);
  const ownerData  = ownerDoc.data();

  if (credit.senderUpiId) {
    await updateDoc(ownerRef, { upiIds: arrayUnion(credit.senderUpiId) });
  } else if (credit.senderName) {
    await updateDoc(ownerRef, { knownSenderNames: arrayUnion(credit.senderName.toLowerCase()) });
  }
  // Bonus known names, so a future transfer may match on the very first try
  if (ownerData.name)           await updateDoc(ownerRef, { knownSenderNames: arrayUnion(ownerData.name.toLowerCase()) });
  if (ownerData.legalOwnerName) await updateDoc(ownerRef, { knownSenderNames: arrayUnion(ownerData.legalOwnerName.toLowerCase()) });
  if (flatId)                   await updateDoc(ownerRef, { knownSenderNames: arrayUnion(`flat${flatId}`.toLowerCase()) });
}

// ════════════════════════════════════════════════════════════════
// LIVE APP BALANCE — design frozen 2026-07-13, updated 2026-07-13
// Calculated fresh from the actual records every time, instead of
// trusting a stored number that can silently drift out of sync.
//
// INCOME  = confirmed payments (maintenance + water)
//         + Other Income entries (maintenanceIncome collection)
//         + paid Society Work dues (adhocPayments, paid = true) —
//           EXCLUDING dues from a DISABLED event (TasksScreen 3.3 fix,
//           2026-07-13): events are never deleted, only marked
//           disabled=true. Their dues stay in Firestore untouched
//           (paid or unpaid), but stop counting toward this total,
//           so a cancelled event's money never pollutes the real balance.
// EXPENSE = paid expenses (maintenance + water tanker + pocket)
//
// NOTE: "eventEntries" (a possible income/expense source tied to
// TasksScreen.jsx events) is NOT included yet — its field names
// haven't been confirmed against the actual code. Left out on
// purpose rather than guessed, so this number is never silently
// wrong. Add it once TasksScreen.jsx is reviewed.
// ════════════════════════════════════════════════════════════════
async function computeLiveAppBalance(openingBalance = 0) {
  const [paySnap, miscSnap, adhocSnap, expSnap] = await Promise.all([
    getDocs(query(collection(db, "payments"), where("status", "==", "confirmed"))),
    getDocs(collection(db, "maintenanceIncome")),
    getDocs(query(collection(db, "adhocPayments"), where("paid", "==", true))),
    getDocs(query(collection(db, "expenses"), where("approvalStatus", "==", "paid"))),
  ]);

  const paymentsIncome = paySnap.docs.reduce((s, d) => s + (Number(d.data().amount) || 0), 0);
  const miscIncome     = miscSnap.docs.reduce((s, d) => s + (Number(d.data().amount) || 0), 0);
  // FIX — Item 1, frozen 2026-07-13: disabled-event dues are NO LONGER
  // excluded here. That money is real cash already in the bank — it only
  // leaves the balance when the Treasurer actually processes the refund
  // (see "Held for Reversal" widget + processRefund() in CollectionsTab,
  // which creates a matching paid expense at that moment).
  const adhocIncome    = adhocSnap.docs
    .reduce((s, d) => s + (Number(d.data().paidAmount ?? d.data().amount) || 0), 0);
  const totalIncome    = paymentsIncome + miscIncome + adhocIncome;

  const totalExpenses  = expSnap.docs.reduce((s, d) => s + (Number(d.data().amount) || 0), 0);

  const appBalance = openingBalance + totalIncome - totalExpenses;

  return appBalance;
}

// ════════════════════════════════════════════════════════════════
// HELD FOR REVERSAL — Item 2, frozen 2026-07-13
// Total of all refund records still waiting to be paid out
// (adhocPayments docs with reversalStatus === "pending", created when
// a Society Work event is disabled after some flats already paid —
// see TasksScreen.jsx handleDelete()). Informational only — does NOT
// change App Balance, since that money is still genuinely in the bank.
// ════════════════════════════════════════════════════════════════
async function computeHeldForReversal() {
  const snap = await getDocs(query(
    collection(db, "adhocPayments"),
    where("reversalStatus", "==", "pending")
  ));
  return snap.docs.reduce((s, d) => s + (Number(d.data().amount) || 0), 0);
}

function todayISO() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function fmtMonth(m) {
  if (!m) return "";
  const [y, mo] = m.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(mo) - 1]}-${String(y).slice(2)}`;
}

// Given a "YYYY-MM" key, returns the key for the month before it.
// Used to show the Water bill's real usage month (it's billed one month later).
function prevMonthKey(m) {
  if (!m) return null;
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(y, mo - 2, 1); // mo-1 is this month (0-indexed), -1 more for previous month
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmt(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN");
}

function tsToDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts.seconds) return new Date(ts.seconds * 1000);
  return new Date(ts);
}

function fmtDate(ts) {
  const d = tsToDate(ts);
  if (!d) return "";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
}

// FY runs Apr → Mar. Returns months in FY order up to current month only (no future months).
function getFYMonths() {
  const now = new Date();
  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const months = [];
  for (let i = 0; i < 12; i++) {
    const m = (3 + i) % 12; // 3 = April (0-indexed)
    const y = fyStartYear + Math.floor((3 + i) / 12);
    const key = `${y}-${String(m + 1).padStart(2, "0")}`;
    if (key > todayKey) break; // stop at current month
    months.push({ key, label: fmtMonth(key) });
  }
  return months;
}

const DEFAULT_REGULAR_CATEGORIES = [
  "Electricity bill",
  "Watchman salary",
  "Garbage collection",
  "Water bill (CAN/Metro)",
  "Association mobile recharge",
  "Bank charges",
];

// ════════════════════════════════════════════════════════════════
// SHARED STYLE BITS
// ════════════════════════════════════════════════════════════════
const S = {
  card: {
    background: "#1a1a24", border: "1px solid #2e2e40",
    borderRadius: "14px", padding: "14px", marginBottom: "10px",
  },
  row: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  sectionTitle: {
    fontSize: "11px", color: "#8888a8", textTransform: "uppercase",
    letterSpacing: "0.5px", fontWeight: "600", marginBottom: "8px", marginTop: "16px",
  },
  input: {
    width: "100%", background: "#0f0f13", border: "1px solid #2e2e40",
    borderRadius: "10px", padding: "10px 12px", color: "#e8e8f0",
    fontSize: "14px", outline: "none", boxSizing: "border-box", fontFamily: "inherit",
  },
  label: { fontSize: "11px", color: "#8888a8", marginBottom: "5px", display: "block", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "600" },
  primaryBtn: {
    width: "100%", background: "#6c63ff", color: "#fff", border: "none",
    borderRadius: "10px", padding: "12px", fontSize: "14px", fontWeight: "700", cursor: "pointer",
  },
  ghostBtn: {
    background: "none", border: "1px solid #2e2e40", color: "#8888a8",
    borderRadius: "8px", padding: "6px 12px", fontSize: "12px", fontWeight: "600", cursor: "pointer",
  },
};

function Badge({ text, color }) {
  const colors = {
    green:  { bg: "#4caf5018", border: "#4caf5040", text: "#4caf50" },
    red:    { bg: "#ff525218", border: "#ff525240", text: "#ff5252" },
    amber:  { bg: "#ffa72618", border: "#ffa72640", text: "#ffa726" },
    blue:   { bg: "#42a5f518", border: "#42a5f540", text: "#42a5f5" },
    purple: { bg: "#6c63ff18", border: "#6c63ff40", text: "#6c63ff" },
    grey:   { bg: "#8888a818", border: "#8888a840", text: "#8888a8" },
  };
  const c = colors[color] || colors.blue;
  return (
    <span style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
      borderRadius: "6px", padding: "2px 8px", fontSize: "11px", fontWeight: "600",
    }}>
      {text}
    </span>
  );
}

export function MonthChips({ months, active, onSelect, extra }) {
  return (
    <div style={{ display: "flex", gap: "6px", marginBottom: "12px", overflowX: "auto", paddingBottom: "4px" }}>
      {extra}
      {months.map(m => (
        <button key={m.key} onClick={() => onSelect(m.key)} style={{
          flexShrink: 0, padding: "6px 12px", borderRadius: "8px", border: "1px solid",
          borderColor: active === m.key ? "#6c63ff" : "#2e2e40",
          background: active === m.key ? "#6c63ff20" : "transparent",
          color: active === m.key ? "#6c63ff" : "#8888a8",
          fontSize: "12px", fontWeight: "600", cursor: "pointer",
        }}>
          {m.label}
        </button>
      ))}
    </div>
  );
}

export function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#000000cc", zIndex: 1000,
      display: "flex", alignItems: "flex-end", justifyContent: "center",
    }}>
      <div style={{
        background: "#1a1a24", borderRadius: "20px 20px 0 0",
        padding: "20px", width: "100%", maxWidth: "640px",
        maxHeight: "90vh", overflowY: "auto",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#e8e8f0" }}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#8888a8", fontSize: "20px", cursor: "pointer" }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// COLLECTIONS — Declare Payment Modal (FlatOwner)
// ════════════════════════════════════════════════════════════════
function DeclareModal({ flatId, ownerName, settings, onClose, onDone }) {
  const { user } = useAuth();
  const fyMonths = getFYMonths();
  const [month, setMonth]   = useState(currentMonthKey());
  const [type, setType]     = useState("Maintenance");
  const [amount, setAmount] = useState(String(settings?.maintenanceAmt || 1500));
  const [upiRef, setUpiRef] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState("");

  useEffect(() => {
    if (type === "Maintenance") setAmount(String(settings?.maintenanceAmt || 1500));
    else if (type === "Water") setAmount("");
    else setAmount(String(settings?.maintenanceAmt || 1500));
  }, [type, settings]);

  async function handleSubmit() {
    if (!upiRef.trim()) { setErr("UPI transaction reference is required."); return; }
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) { setErr("Enter a valid amount."); return; }
    setSaving(true);
    try {
      await addDoc(collection(db, "payments"), {
        flatId, month, type, amount: Number(amount), upiRef: upiRef.trim(),
        status: "declared", declaredBy: user.name, declaredAt: serverTimestamp(), confirmedBy: null,
        autoConfirmed: false,
      });
      await addDoc(collection(db, "chat"), {
        flatId, senderName: user.name, senderRole: user.role, roleIcon: "🏠",
        message: `💳 Payment Declared — ${type} · ${fmtMonth(month)} · ${fmt(Number(amount))}. Ref: ${upiRef.trim()}. Please confirm.`,
        timestamp: serverTimestamp(),
      });
      onDone();
    } catch (e) {
      setErr("Failed to submit. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Declare Payment" onClose={onClose}>
      <div style={{ display: "flex", gap: "8px", marginBottom: "14px" }}>
        <div style={{ background: "#6c63ff18", border: "1px solid #6c63ff40", borderRadius: "10px", padding: "10px 14px" }}>
          <div style={{ fontSize: "11px", color: "#8888a8" }}>Flat</div>
          <div style={{ fontSize: "15px", fontWeight: "700", color: "#6c63ff" }}>{flatId}</div>
        </div>
        <div style={{ background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "10px", padding: "10px 14px", flex: 1 }}>
          <div style={{ fontSize: "11px", color: "#8888a8" }}>Resident</div>
          <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>{ownerName}</div>
        </div>
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={S.label}>For Month</label>
        <select value={month} onChange={e => setMonth(e.target.value)} style={S.input}>
          {fyMonths.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={S.label}>Payment Type</label>
        <div style={{ display: "flex", gap: "8px" }}>
          {["Maintenance", "Water", "both"].map(t => (
            <button key={t} onClick={() => setType(t)} style={{
              flex: 1, padding: "8px", borderRadius: "10px", border: "1px solid",
              borderColor: type === t ? "#6c63ff" : "#2e2e40",
              background: type === t ? "#6c63ff20" : "transparent",
              color: type === t ? "#6c63ff" : "#8888a8",
              fontSize: "12px", fontWeight: "600", cursor: "pointer",
            }}>
              {t === "both" ? "Both" : t}
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={S.label}>Amount (₹)</label>
        <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Enter amount" style={S.input} />
      </div>

      <div style={{ marginBottom: "20px" }}>
        <label style={S.label}>UPI Transaction Reference</label>
        <input type="text" value={upiRef} onChange={e => setUpiRef(e.target.value)} placeholder="e.g. 123456789012" style={S.input} />
        <div style={{ fontSize: "11px", color: "#555570", marginTop: "4px" }}>
          Enter the UPI reference number from your payment app exactly — the MC's Gmail sync will match it and auto-confirm.
        </div>
      </div>

      {err && (
        <div style={{ background: "#ff525218", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 12px", marginBottom: "12px", color: "#ff5252", fontSize: "13px" }}>
          {err}
        </div>
      )}

      <button onClick={handleSubmit} disabled={saving} style={{ ...S.primaryBtn, padding: "14px", fontSize: "15px", opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }}>
        {saving ? "Submitting…" : "Submit Declaration"}
      </button>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════
// COLLECTIONS — Confirm / Reject Declaration Modal (MC)
// ════════════════════════════════════════════════════════════════
function ConfirmModal({ payment, onClose, onDone }) {
  const { user } = useAuth();
  const [action, setAction] = useState("confirm");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleAction() {
    setSaving(true);
    try {
      await updateDoc(doc(db, "payments", payment.id), {
        status: action === "confirm" ? "confirmed" : "rejected",
        confirmedBy: user.name, confirmedAt: serverTimestamp(),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onDone();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Review Declaration" onClose={onClose}>
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
          <div>
            <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>Flat {payment.flatId}</div>
            <div style={{ fontSize: "12px", color: "#8888a8", marginTop: "2px" }}>{payment.declaredBy}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "18px", fontWeight: "700", color: "#ffa726" }}>{fmt(payment.amount)}</div>
            <div style={{ fontSize: "11px", color: "#8888a8" }}>{payment.type} · {fmtMonth(payment.month)}</div>
          </div>
        </div>
        <div style={{ background: "#0f0f13", borderRadius: "8px", padding: "8px 12px" }}>
          <div style={{ fontSize: "11px", color: "#8888a8" }}>UPI Ref</div>
          <div style={{ fontSize: "13px", color: "#42a5f5", fontFamily: "monospace", marginTop: "2px" }}>{payment.upiRef}</div>
        </div>
        <div style={{ fontSize: "11px", color: "#555570", marginTop: "8px" }}>Declared on {fmtDate(payment.declaredAt)}</div>
      </div>

      <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        {["confirm", "reject"].map(a => (
          <button key={a} onClick={() => setAction(a)} style={{
            flex: 1, padding: "10px", borderRadius: "10px", border: "1px solid",
            borderColor: action === a ? (a === "confirm" ? "#4caf50" : "#ff5252") : "#2e2e40",
            background: action === a ? (a === "confirm" ? "#4caf5020" : "#ff525220") : "transparent",
            color: action === a ? (a === "confirm" ? "#4caf50" : "#ff5252") : "#8888a8",
            fontSize: "13px", fontWeight: "600", cursor: "pointer",
          }}>
            {a === "confirm" ? "✅ Confirm" : "❌ Reject"}
          </button>
        ))}
      </div>

      {action === "reject" && (
        <div style={{ marginBottom: "16px" }}>
          <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Reason for rejection (optional)" style={S.input} />
        </div>
      )}

      <button onClick={handleAction} disabled={saving} style={{
        ...S.primaryBtn, padding: "14px", fontSize: "15px",
        background: saving ? "#2e2e40" : (action === "confirm" ? "#4caf50" : "#ff5252"),
        cursor: saving ? "not-allowed" : "pointer",
      }}>
        {saving ? "Processing…" : (action === "confirm" ? "Confirm Payment" : "Reject & Notify")}
      </button>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════
// COLLECTIONS — Map Unmatched Credit Modal (Treasurer/President)
// ════════════════════════════════════════════════════════════════
function MapUnmatchedModal({ credit, flats, onClose, onDone }) {
  const { user } = useAuth();
  const [paymentType, setPaymentType] = useState("monthly"); // "monthly" | "society_work"
  const [flatId, setFlatId]           = useState("");
  const [month, setMonth]             = useState(currentMonthKey());
  const [events, setEvents]           = useState([]);
  const [eventId, setEventId]         = useState("");
  const [saving, setSaving]           = useState(false);
  const [err, setErr]                 = useState("");
  const fyMonths = getFYMonths();

  // Load open society work events when Society Work tab selected
  useEffect(() => {
    if (paymentType !== "society_work") return;
    async function loadEvents() {
      const snap = await getDocs(query(
        collection(db, "adhocEvents"),
        where("status", "==", "open"),
        where("category", "==", "Society Work")
      ));
      setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }
    loadEvents();
  }, [paymentType]);

  async function handleMap() {
    if (!flatId) { setErr("Please select a flat."); return; }
    if (paymentType === "monthly" && !month) { setErr("Please select a month."); return; }
    if (paymentType === "society_work" && !eventId) { setErr("Please select a Society Work event."); return; }
    setSaving(true);
    try {
      if (paymentType === "monthly") {
        // Save as confirmed maintenance payment
        await addDoc(collection(db, "payments"), {
          flatId, month, type: "maintenance", amount: credit.amount,
          upiRef: credit.gmailMsgId || "", status: "confirmed",
          declaredBy: "Gmail (auto)", declaredAt: serverTimestamp(),
          confirmedBy: user.name, confirmedAt: serverTimestamp(),
          gmailMsgId: credit.gmailMsgId || "", senderUpiId: credit.senderUpiId || "",
          autoConfirmed: false,
        });
        // Remember this payer for next time (saved on the flat owner's own
        // record, which is what the automatic checker actually reads)
        await learnPayerForFlat(flatId, credit);
      } else {
        // Find the existing adhocPayments record for this flat + event and update it
        const existingSnap = await getDocs(query(
          collection(db, "adhocPayments"),
          where("flatId", "==", flatId),
          where("eventId", "==", eventId)
        ));
        if (existingSnap.empty) {
          setErr("No Society Work due found for this flat and event. Please check and try again.");
          setSaving(false);
          return;
        }
        const existingDoc = existingSnap.docs[0];
        const originalAmt = existingDoc.data().amount || 0;
        const paidAmt = credit.amount;
        const shortfall = originalAmt - paidAmt;
        await updateDoc(doc(db, "adhocPayments", existingDoc.id), {
          paid: true,
          paidDate: todayISO(),
          paidBy: user.name,
          paidVia: "IMPS/NEFT",
          paidAmount: paidAmt,
          shortfall: shortfall > 0 ? shortfall : 0,
          confirmedBy: user.name,
          confirmedAt: serverTimestamp(),
          gmailMsgId: credit.gmailMsgId || "",
          senderUpiId: credit.senderUpiId || "",
          note: shortfall > 0
            ? `Partial payment. ₹${shortfall} still pending. Mapped from unmatched Gmail credit.`
            : "Mapped from unmatched Gmail credit.",
        });
        // Remember this payer for next time (saved on the flat owner's own
        // record, which is what the automatic checker actually reads)
        await learnPayerForFlat(flatId, credit);
      }

      // Mark credit as mapped in both cases
      await updateDoc(doc(db, "unmatchedCredits", credit.id), {
        status: "mapped", mappedTo: flatId,
        mappedType: paymentType,
        mappedBy: user.name, mappedAt: serverTimestamp(),
      });
      onDone();
    } catch (e) {
      setErr("Failed to map payment. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Map Unmatched Payment" onClose={onClose}>
      {/* Payment info card */}
      <div style={S.card}>
        <div style={S.row}>
          <div>
            <div style={{ fontSize: "12px", color: "#8888a8" }}>Sender UPI ID</div>
            <div style={{ fontSize: "13px", color: "#42a5f5", fontFamily: "monospace", marginTop: "2px" }}>{credit.senderUpiId || "—"}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "18px", fontWeight: "700", color: "#ffa726" }}>{fmt(credit.amount)}</div>
            <div style={{ fontSize: "11px", color: "#8888a8" }}>{fmtDate(credit.date)}</div>
          </div>
        </div>
      </div>

      {/* Payment Type selector */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        {[["monthly", "📅 Monthly Due"], ["society_work", "🏗 Society Work"]].map(([val, label]) => (
          <button key={val} onClick={() => { setPaymentType(val); setFlatId(""); setEventId(""); setErr(""); }}
            style={{ flex: 1, padding: "10px", borderRadius: "10px", border: "none", cursor: "pointer", fontSize: "13px", fontWeight: "600",
              background: paymentType === val ? "#7c6af7" : "#1e1e2e", color: paymentType === val ? "#fff" : "#8888a8" }}>
            {label}
          </button>
        ))}
      </div>

      {/* Flat selector — always shown */}
      <div style={{ marginBottom: "14px" }}>
        <label style={S.label}>Map to Flat</label>
        <select value={flatId} onChange={e => setFlatId(e.target.value)} style={S.input}>
          <option value="">— select flat —</option>
          {flats.map(f => <option key={f.flatId} value={f.flatId}>Flat {f.flatId} — {f.ownerName}</option>)}
        </select>
      </div>

      {/* Monthly Due — show month selector */}
      {paymentType === "monthly" && (
        <div style={{ marginBottom: "16px" }}>
          <label style={S.label}>For Month</label>
          <select value={month} onChange={e => setMonth(e.target.value)} style={S.input}>
            {fyMonths.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
      )}

      {/* Society Work — show event selector */}
      {paymentType === "society_work" && (
        <div style={{ marginBottom: "16px" }}>
          <label style={S.label}>Society Work Event</label>
          <select value={eventId} onChange={e => setEventId(e.target.value)} style={S.input}>
            <option value="">— select event —</option>
            {events.map(e => <option key={e.id} value={e.id}>{e.title}</option>)}
          </select>
          {events.length === 0 && (
            <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "4px" }}>No open Society Work events found.</div>
          )}
        </div>
      )}

      {/* Info note */}
      <div style={{ background: "#42a5f510", border: "1px solid #42a5f530", borderRadius: "10px", padding: "10px 14px", fontSize: "12px", color: "#42a5f5", marginBottom: "16px" }}>
        {paymentType === "monthly"
          ? `💡 This UPI ID will be remembered for Flat ${flatId || "—"}'s future payments — auto-confirmed next time.`
          : "💡 Payment will be recorded as Society Work contribution and linked to the selected event."}
      </div>

      {err && (
        <div style={{ background: "#ff525218", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 12px", marginBottom: "12px", color: "#ff5252", fontSize: "13px" }}>
          {err}
        </div>
      )}

      <button onClick={handleMap} disabled={saving} style={{ ...S.primaryBtn, padding: "14px", fontSize: "15px", opacity: saving ? 0.7 : 1 }}>
        {saving ? "Mapping…" : "Map & Confirm Payment"}
      </button>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════
// COLLECTIONS — Approve Overpayment Modal (President)
// ════════════════════════════════════════════════════════════════
function ApproveOverpaymentModal({ payment, onClose, onDone }) {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);

  async function handleApprove() {
    setSaving(true);
    try {
      await updateDoc(doc(db, "payments", payment.id), {
        overpaymentStatus: "approved", overpaymentApprovedBy: user.name,
      });
      onDone();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Approve Overpayment Brought-Forward" onClose={onClose}>
      <div style={S.card}>
        <div style={S.row}>
          <div>
            <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>Flat {payment.flatId}</div>
            <div style={{ fontSize: "12px", color: "#8888a8", marginTop: "2px" }}>{fmtMonth(payment.month)} · Paid {fmt(payment.amount)}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "11px", color: "#8888a8" }}>Surplus</div>
            <div style={{ fontSize: "18px", fontWeight: "700", color: "#ffa726" }}>{fmt(payment.overpaymentAmt)}</div>
          </div>
        </div>
      </div>
      <div style={{ background: "#42a5f510", border: "1px solid #42a5f530", borderRadius: "10px", padding: "10px 14px", fontSize: "12px", color: "#42a5f5", marginBottom: "16px" }}>
        💡 Approving will bring this surplus forward as a credit against this flat's future dues.
      </div>
      <button onClick={handleApprove} disabled={saving} style={{ ...S.primaryBtn, padding: "14px", fontSize: "15px", background: saving ? "#2e2e40" : "#4caf50" }}>
        {saving ? "Approving…" : "✅ Approve Brought-Forward"}
      </button>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════
// COLLECTIONS SUB-TAB
// ════════════════════════════════════════════════════════════════
function CollectionsTab({ user, isMC, isPresident, isTreasurer, settings, flats, refreshKey, setRefreshKey }) {
  const fyMonths = getFYMonths();
  const [payments, setPayments]             = useState([]);
  const [allPayments, setAllPayments]       = useState([]);
  const [maintenanceIncome, setMaintenanceIncome] = useState([]);
  const [unmatched, setUnmatched]           = useState([]);
  const [pendingReversals, setPendingReversals] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filterMonth, setFilterMonth] = useState(currentMonthKey());
  const fyStartMonth = getFYMonths()[0]?.key || "2026-04";
  const [showDeclare, setShowDeclare] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [mappingCredit, setMappingCredit] = useState(null);
  const [approvingOverpay, setApprovingOverpay] = useState(null);
  const [processingRefund, setProcessingRefund] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  const myFlat = flats.find(f => f.flatId === user.flatId);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        if (isMC) {
          const snap = await getDocs(query(collection(db, "payments"), where("month", "==", filterMonth)));
          setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() })));

          const uSnap = await getDocs(query(collection(db, "unmatchedCredits"), where("status", "==", "unmatched")));
          setUnmatched(uSnap.docs.map(d => ({ id: d.id, ...d.data() })));

          // All confirmed payments Apr to selected month — for cumulative income chip
          const allSnap = await getDocs(query(
            collection(db, "payments"),
            where("month", ">=", fyStartMonth),
            where("month", "<=", filterMonth),
            where("status", "==", "confirmed")
          ));
          setAllPayments(allSnap.docs.map(d => ({ id: d.id, ...d.data() })));

          // Misc income for selected month
          const miscSnap = await getDocs(query(
            collection(db, "maintenanceIncome"),
            where("month", "==", filterMonth)
          ));
          setMaintenanceIncome(miscSnap.docs.map(d => ({ id: d.id, ...d.data() })));

          // Item 3 — pending refund records (Treasurer only), not month-scoped
          if (isTreasurer) {
            const revSnap = await getDocs(query(
              collection(db, "adhocPayments"),
              where("reversalStatus", "==", "pending")
            ));
            setPendingReversals(revSnap.docs.map(d => ({ id: d.id, ...d.data() })));
          }
        } else {
          const snap = await getDocs(query(collection(db, "payments"), where("flatId", "==", user.flatId)));
          const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          all.sort((a, b) => (b.month || "").localeCompare(a.month || ""));
          setPayments(all);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [isMC, isTreasurer, user.flatId, filterMonth, refreshKey]);

  // Item 3 — Treasurer marks a pending refund as sent. Two things happen in
  // one atomic batch: the refund record is marked processed (reversalStatus
  // "approved", who + when), and a matching PAID expense is created so
  // App Balance goes down by exactly this amount at exactly this moment
  // (ties back into Item 1's fix — money only leaves the balance when it
  // actually leaves the bank).
  async function processRefund(rev) {
    if (!window.confirm(`Confirm ₹${Number(rev.amount || 0).toLocaleString("en-IN")} refund sent to Flat ${rev.flatId} for "${rev.eventTitle || "Society Work"}"?`)) return;
    setProcessingRefund(rev.id);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, "adhocPayments", rev.id), {
        reversalStatus: "approved",
        reversalApprovedBy: user.name,
        reversalDate: todayISO(),
      });
      const expRef = doc(collection(db, "expenses"));
      batch.set(expRef, {
        category: `Refund — ${rev.eventTitle || "Society Work"}`,
        amount: Number(rev.amount || 0),
        month: currentMonthKey(),
        approvalStatus: "paid",
        isPocket: false,
        flatId: rev.flatId,
        description: `Refund to Flat ${rev.flatId} for disabled event "${rev.eventTitle || "Society Work"}"`,
        addedBy: user.name,
        paidAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      });
      await batch.commit();
      setRefreshKey(k => k + 1);
    } catch (e) {
      console.error(e);
      alert("Failed to process refund. Please try again.");
    } finally {
      setProcessingRefund(null);
    }
  }

  // NEW: Triggers the GitHub Actions Gmail sync via our Cloudflare helper
  async function handleNewSync() {
    setSyncing(true);
    setSyncMsg("");
    try {
      const response = await fetch(process.env.REACT_APP_SYNC_TRIGGER_URL, {
        method: "POST",
        headers: { "X-Trigger-Key": process.env.REACT_APP_SYNC_TRIGGER_KEY },
      });
      if (response.ok) {
        setSyncMsg("✅ Sync started — check back in a minute or two.");
      } else {
        setSyncMsg("⚠ Sync failed to start. Please try again.");
      }
    } catch (e) {
      console.error(e);
      setSyncMsg(`⚠ Sync failed: ${e.message || "Please try again."}`);
    } finally {
      setSyncing(false);
    }
  }

  if (loading) {
    return <div style={{ textAlign: "center", padding: "60px 20px", color: "#555570", fontSize: "13px" }}>Loading collections…</div>;
  }

  // ── MC / President view ──────────────────────────────────────
  if (isMC) {
    const confirmedThisMonth = payments.filter(p => p.status === "confirmed");
    const declared = payments.filter(p => p.status === "declared");
    const collected = confirmedThisMonth.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const paidFlatIds = new Set(confirmedThisMonth.map(p => p.flatId));
    const outstandingFlats = flats.filter(f => !paidFlatIds.has(f.flatId));
    const pendingOverpayments = payments.filter(p => p.overpaymentStatus === "pending");

    // Cumulative income chip calculations
    const prevMonthsPayments = allPayments.filter(p => p.month < filterMonth);
    const prevCollected = prevMonthsPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const totalIncome = prevCollected + collected;
    const fyStart = fmtMonth(fyStartMonth);
    const prevMonthLabel = getFYMonths().filter(m => m.key < filterMonth).slice(-1)[0]?.label || null;

    return (
      <div>
        {/* Header + Sync */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
          <div>
            <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>💰 Income</div>
            <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>
              {settings?.lastGmailSync ? `Last synced ${fmtDate(settings.lastGmailSync)} ${tsToDate(settings.lastGmailSync)?.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}` : "Tap Sync to check Gmail for new payments"}
            </div>
          </div>
          <button onClick={handleNewSync} disabled={syncing} style={S.ghostBtn}>
            {syncing ? "Syncing…" : "🔄 Sync"}
          </button>
        </div>
        {syncMsg && (
          <div style={{
            fontSize: "11px", color: syncMsg.startsWith("⚠") ? "#ff5252" : "#4caf50",
            marginBottom: "12px",
          }}>
            {syncMsg}
          </div>
        )}

        {/* Unmatched credits */}
        {unmatched.length > 0 && (
          <>
            <div style={S.sectionTitle}>⚠ Unmatched Payments ({unmatched.length})</div>
            {unmatched.map(c => (
              <div key={c.id} style={{ ...S.card, border: "1px solid #ffa72640" }}>
                <div style={S.row}>
                  <div>
                    <div style={{ fontSize: "13px", color: "#e8e8f0", fontWeight: "600" }}>{c.senderUpiId || "Unknown sender"}</div>
                    <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>{fmtDate(c.date)}</div>
                  </div>
                  <div style={{ fontSize: "16px", fontWeight: "700", color: "#ffa726" }}>{fmt(c.amount)}</div>
                </div>
                <button onClick={() => setMappingCredit(c)} style={{ ...S.primaryBtn, marginTop: "10px", padding: "9px", fontSize: "12px", background: "#ffa72620", border: "1px solid #ffa72640", color: "#ffa726" }}>
                  Map to Flat →
                </button>
              </div>
            ))}
          </>
        )}

        {/* Overpayment approvals — President only */}
        {isPresident && pendingOverpayments.length > 0 && (
          <>
            <div style={S.sectionTitle}>🔔 Overpayment Approvals</div>
            {pendingOverpayments.map(p => (
              <div key={p.id} style={{ ...S.card, border: "1px solid #6c63ff40" }}>
                <div style={S.row}>
                  <div>
                    <div style={{ fontSize: "13px", color: "#e8e8f0", fontWeight: "600" }}>Flat {p.flatId}</div>
                    <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>Paid {fmt(p.amount)} · {fmtMonth(p.month)}</div>
                  </div>
                  <div style={{ fontSize: "16px", fontWeight: "700", color: "#6c63ff" }}>+{fmt(p.overpaymentAmt)}</div>
                </div>
                <button onClick={() => setApprovingOverpay(p)} style={{ ...S.primaryBtn, marginTop: "10px", padding: "9px", fontSize: "12px" }}>
                  Approve Brought-Forward →
                </button>
              </div>
            ))}
          </>
        )}

        {/* Pending Reversals — Treasurer only (Item 3, frozen 2026-07-13) */}
        {isTreasurer && pendingReversals.length > 0 && (
          <>
            <div style={S.sectionTitle}>↩ Pending Refunds ({pendingReversals.length})</div>
            {pendingReversals.map(rev => (
              <div key={rev.id} style={{ ...S.card, border: "1px solid #ffa72640" }}>
                <div style={S.row}>
                  <div>
                    <div style={{ fontSize: "13px", color: "#e8e8f0", fontWeight: "600" }}>Flat {rev.flatId}</div>
                    <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>{rev.eventTitle || "Society Work"} · event disabled</div>
                  </div>
                  <div style={{ fontSize: "16px", fontWeight: "700", color: "#ffa726" }}>{fmt(rev.amount)}</div>
                </div>
                <button
                  onClick={() => processRefund(rev)}
                  disabled={processingRefund === rev.id}
                  style={{ ...S.primaryBtn, marginTop: "10px", padding: "9px", fontSize: "12px", background: "#ffa72620", border: "1px solid #ffa72640", color: "#ffa726" }}
                >
                  {processingRefund === rev.id ? "Processing…" : "✓ Refund Sent — Mark Done"}
                </button>
              </div>
            ))}
          </>
        )}

        {/* Month chips */}
        <div style={S.sectionTitle}>Month</div>
        <MonthChips months={fyMonths} active={filterMonth} onSelect={setFilterMonth} />

        {/* Stats */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
          <div style={{ flex: 1, background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px", padding: "10px", textAlign: "center" }}>
            <div style={{ fontSize: "18px", fontWeight: "700", color: "#4caf50" }}>{fmt(collected)}</div>
            <div style={{ fontSize: "10px", color: "#8888a8", marginTop: "2px" }}>Collected</div>
          </div>
          <div style={{ flex: 1, background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px", padding: "10px", textAlign: "center" }}>
            <div style={{ fontSize: "18px", fontWeight: "700", color: "#ffa726" }}>{declared.length}</div>
            <div style={{ fontSize: "10px", color: "#8888a8", marginTop: "2px" }}>Awaiting Confirm</div>
          </div>
          <div style={{ flex: 1, background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px", padding: "10px", textAlign: "center" }}>
            <div style={{ fontSize: "18px", fontWeight: "700", color: "#ff5252" }}>{outstandingFlats.length}</div>
            <div style={{ fontSize: "10px", color: "#8888a8", marginTop: "2px" }}>Outstanding</div>
          </div>
        </div>

        {/* Cumulative Income chip — 3-column */}
        <div style={{ background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px", padding: "12px 16px", marginBottom: "16px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", textAlign: "center" }}>
            <div style={{ borderRight: "1px solid #2e2e40", paddingRight: "8px" }}>
              <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "2px" }}>Cumulative</div>
              <div style={{ fontSize: "10px", color: "#555570", marginBottom: "6px" }}>{prevMonthLabel ? `(${fyStart} to ${prevMonthLabel})` : `(${fyStart})`}</div>
              <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>{fmt(prevCollected)}</div>
            </div>
            <div style={{ borderRight: "1px solid #2e2e40", paddingRight: "8px" }}>
              <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "2px" }}>This Month</div>
              <div style={{ fontSize: "10px", color: "#555570", marginBottom: "6px" }}>({fmtMonth(filterMonth)})</div>
              <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>{fmt(collected)}</div>
            </div>
            <div>
              <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "2px" }}>Total Income</div>
              <div style={{ fontSize: "10px", color: "#555570", marginBottom: "6px" }}>&nbsp;</div>
              <div style={{ fontSize: "14px", fontWeight: "700", color: "#4caf50" }}>{fmt(totalIncome)}</div>
            </div>
          </div>
        </div>

        {/* Pending declarations */}
        {declared.length > 0 && (
          <>
            <div style={S.sectionTitle}>Pending Confirmations</div>
            {declared.map(p => (
              <div key={p.id} style={{ ...S.card, border: "1px solid #ffa72630" }}>
                <div style={S.row}>
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0" }}>Flat {p.flatId}</div>
                    <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>{p.type} · {p.declaredBy}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>{fmt(p.amount)}</div>
                    <Badge text="Declared" color="amber" />
                  </div>
                </div>
                <button onClick={() => setSelectedPayment(p)} style={{ ...S.primaryBtn, marginTop: "10px", padding: "9px", fontSize: "12px", background: "#ffa72620", border: "1px solid #ffa72640", color: "#ffa726" }}>
                  Review →
                </button>
              </div>
            ))}
          </>
        )}

        {/* All flats — status for this month */}
        <div style={S.sectionTitle}>All Flats — {fmtMonth(filterMonth)}</div>
        {flats.map(f => {
          const p = payments.find(pp => pp.flatId === f.flatId);
          const status = p?.status || "pending";
          const color = status === "confirmed" ? "green" : status === "declared" ? "amber" : "red";
          const label = status === "confirmed" ? "Paid" : status === "declared" ? "Declared" : "Pending";
          return (
            <div key={f.flatId} style={{
              ...S.card, marginBottom: "6px", padding: "10px 14px",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <div>
                <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0" }}>Flat {f.flatId}</div>
                <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "1px" }}>{f.ownerName}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "13px", fontWeight: "700", color: "#e8e8f0" }}>{fmt(p?.amount ?? f.maintenanceRate)}</div>
                <Badge text={label} color={color} />
              </div>
            </div>
          );
        })}

        {/* Other Income — misc entries from maintenanceIncome collection */}
        {maintenanceIncome.length > 0 && (
          <>
            <div style={S.sectionTitle}>💼 Other Income — {fmtMonth(filterMonth)}</div>
            {maintenanceIncome.map(i => (
              <div key={i.id} style={{ ...S.card, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>{i.type}</div>
                  <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>
                    {i.description}{i.addedBy ? ` · ${i.addedBy}` : ""}
                  </div>
                </div>
                <div style={{ fontSize: "14px", fontWeight: "700", color: "#4caf50" }}>{fmt(i.amount)}</div>
              </div>
            ))}
          </>
        )}

        {selectedPayment && (
          <ConfirmModal payment={selectedPayment} onClose={() => setSelectedPayment(null)} onDone={() => { setSelectedPayment(null); setRefreshKey(k => k + 1); }} />
        )}
        {mappingCredit && (
          <MapUnmatchedModal credit={mappingCredit} flats={flats} onClose={() => setMappingCredit(null)} onDone={() => { setMappingCredit(null); setRefreshKey(k => k + 1); }} />
        )}
        {approvingOverpay && (
          <ApproveOverpaymentModal payment={approvingOverpay} onClose={() => setApprovingOverpay(null)} onDone={() => { setApprovingOverpay(null); setRefreshKey(k => k + 1); }} />
        )}
      </div>
    );
  }

  // ── Flat Owner view ──────────────────────────────────────────
  const myThisMonth = payments.find(p => p.month === currentMonthKey());
  const isPaid     = myThisMonth?.status === "confirmed";
  const isDeclared = myThisMonth?.status === "declared";
  const maintenanceAmt = myFlat?.maintenanceRate || settings?.maintenanceAmt || 1500;
  let statusColor = "#ffa726", statusText = "Pending", statusBadge = "amber";
  if (isPaid)          { statusColor = "#4caf50"; statusText = "Paid ✅"; statusBadge = "green"; }
  else if (isDeclared) { statusColor = "#ffa726"; statusText = "Declared — Awaiting MC"; statusBadge = "amber"; }

  return (
    <div>
      <div style={{ marginBottom: "12px" }}>
        <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>💰 My Dues</div>
        <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>Flat {user.flatId} · {fmtMonth(currentMonthKey())}</div>
      </div>

      <div style={{
        background: "#1a1a24", border: `1px solid ${statusColor}40`, borderLeft: `3px solid ${statusColor}`,
        borderRadius: "14px", padding: "16px", marginBottom: "16px",
      }}>
        <div style={S.row}>
          <div>
            <div style={{ fontSize: "12px", color: "#8888a8", marginBottom: "4px" }}>Maintenance Due</div>
            <div style={{ fontSize: "26px", fontWeight: "700", color: statusColor }}>
              {isPaid ? fmt(myThisMonth?.amount) : fmt(maintenanceAmt)}
            </div>
            <div style={{ fontSize: "11px", color: "#555570", marginTop: "4px" }}>Due by {settings?.dueCutoffDay || 10}th of each month</div>
          </div>
          <Badge text={statusText} color={statusBadge} />
        </div>

        {!isPaid && !isDeclared && (
          <button onClick={() => setShowDeclare(true)} style={{ ...S.primaryBtn, marginTop: "14px", padding: "11px" }}>
            Declare Payment
          </button>
        )}
        {isDeclared && (
          <div style={{ marginTop: "12px", background: "#ffa72610", border: "1px solid #ffa72630", borderRadius: "8px", padding: "8px 12px", fontSize: "12px", color: "#ffa726", textAlign: "center" }}>
            ⏳ Payment declared — waiting for MC confirmation
          </div>
        )}
        {isPaid && (
          <div style={{ marginTop: "12px", background: "#4caf5010", border: "1px solid #4caf5030", borderRadius: "8px", padding: "8px 12px", fontSize: "12px", color: "#4caf50", textAlign: "center" }}>
            ✅ Payment confirmed by MC
          </div>
        )}
      </div>

      {/* How to pay */}
      <div style={S.sectionTitle}>How to Pay</div>
      <div style={S.card}>
        {[
          ["Bank Account", settings?.bankAccount],
          ["IFSC", settings?.ifsc],
          ["UPI ID", settings?.upi1],
          ["Alt. UPI", settings?.upi2],
        ].filter(([, v]) => v).map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #1e1e2e", fontSize: "12px" }}>
            <span style={{ color: "#8888a8" }}>{k}</span>
            <span style={{ color: "#e8e8f0", fontWeight: "600", fontFamily: "monospace" }}>{v}</span>
          </div>
        ))}
        <div style={{ fontSize: "11px", color: "#555570", marginTop: "8px" }}>
          💡 Pay via UPI and enter the UPI reference number when you declare — the MC's next Gmail sync will auto-confirm it.
        </div>
      </div>

      {/* Payment history */}
      <div style={S.sectionTitle}>Payment History</div>
      {payments.length === 0 ? (
        <div style={{ textAlign: "center", padding: "30px", color: "#555570", fontSize: "13px" }}>No payment history yet.</div>
      ) : (
        payments.map(p => {
          const color = p.status === "confirmed" ? "green" : p.status === "declared" ? "amber" : "red";
          const label = p.status === "confirmed" ? "Paid" : p.status === "declared" ? "Declared" : "Rejected";
          return (
            <div key={p.id} style={{ ...S.card, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>{fmtMonth(p.month)}</div>
                <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>{p.type}{p.autoConfirmed ? " · Gmail auto" : ""}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>{fmt(p.amount)}</div>
                <Badge text={label} color={color} />
              </div>
            </div>
          );
        })
      )}

      {showDeclare && (
        <DeclareModal flatId={user.flatId} ownerName={myFlat?.ownerName || user.name} settings={settings}
          onClose={() => setShowDeclare(false)} onDone={() => { setShowDeclare(false); setRefreshKey(k => k + 1); }} />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// EXPENSES — Add Expense Modal
// ════════════════════════════════════════════════════════════════
export function AddExpenseModal({ user, isMC, regularCategories, onClose, onDone }) {
  const fyMonths = getFYMonths();
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayISO());
  const [month, setMonth] = useState(currentMonthKey());
  const [isRegular, setIsRegular] = useState(isMC);
  const [category, setCategory] = useState(regularCategories[0] || "");
  const [isPocket, setIsPocket] = useState(!isMC);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function handleSubmit() {
    if (!description.trim()) { setErr("Enter a description."); return; }
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) { setErr("Enter a valid amount."); return; }

    setSaving(true);
    try {
      const approvalStatus = isRegular ? "approved" : "pending_president";
      await addDoc(collection(db, "expenses"), {
        description: description.trim(), amount: Number(amount), date, month,
        category: isRegular ? category : "Non-regular",
        isRegular, isPocket, approvalStatus,
        approvedBy: isRegular ? user.name : null,
        approvedAt: isRegular ? serverTimestamp() : null,
        paidAt: null,
        addedBy: user.name, addedByUid: user.uid, addedAt: serverTimestamp(),
        isReimbursed: false,
        // FIX (frozen 2026-07-13): Dashboard filters all expenses by societyId —
        // without this, the expense is saved fine but invisible to the Dashboard's
        // EXPENSES/BALANCE cards, even after a hard refresh.
        societyId: user.societyId,
      });
      onDone();
    } catch (e) {
      setErr("Failed to save expense. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Add Expense" onClose={onClose}>
      <div style={{ marginBottom: "14px" }}>
        <label style={S.label}>Description</label>
        <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. Electricity bill — June" style={S.input} />
      </div>

      <div style={{ display: "flex", gap: "10px", marginBottom: "14px" }}>
        <div style={{ flex: 1 }}>
          <label style={S.label}>Amount (₹)</label>
          <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" style={S.input} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={S.label}>Date</label>
          <input type="date" value={date} onChange={e => { setDate(e.target.value); setMonth(e.target.value.slice(0, 7)); }} style={S.input} />
        </div>
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={S.label}>For Month</label>
        <select value={month} onChange={e => setMonth(e.target.value)} style={S.input}>
          {fyMonths
            .filter(o => isMC || o.key <= currentMonthKey())
            .map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </div>

      {isMC && (
        <div style={{ marginBottom: "14px" }}>
          <label style={S.label}>Type</label>
          <div style={{ display: "flex", gap: "8px" }}>
            {[{ v: true, t: "Regular" }, { v: false, t: "Non-regular" }].map(o => (
              <button key={o.t} onClick={() => setIsRegular(o.v)} style={{
                flex: 1, padding: "8px", borderRadius: "10px", border: "1px solid",
                borderColor: isRegular === o.v ? "#6c63ff" : "#2e2e40",
                background: isRegular === o.v ? "#6c63ff20" : "transparent",
                color: isRegular === o.v ? "#6c63ff" : "#8888a8",
                fontSize: "12px", fontWeight: "600", cursor: "pointer",
              }}>
                {o.t}
              </button>
            ))}
          </div>
          <div style={{ fontSize: "11px", color: "#555570", marginTop: "6px" }}>
            {isRegular ? "Regular expenses are auto-approved (Treasurer level)." : "Non-regular expenses need President's approval before payment."}
          </div>
        </div>
      )}

      {isMC && isRegular && (
        <div style={{ marginBottom: "14px" }}>
          <label style={S.label}>Category</label>
          <select value={category} onChange={e => setCategory(e.target.value)} style={S.input}>
            {regularCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}

      {isMC && (
        <div style={{ marginBottom: "14px" }}>
          <label style={S.label}>Paid From</label>
          <div style={{ display: "flex", gap: "8px" }}>
            {[{ v: false, t: "Society Account" }, { v: true, t: "MC Member's Pocket" }].map(o => (
              <button key={o.t} onClick={() => setIsPocket(o.v)} style={{
                flex: 1, padding: "8px", borderRadius: "10px", border: "1px solid",
                borderColor: isPocket === o.v ? "#6c63ff" : "#2e2e40",
                background: isPocket === o.v ? "#6c63ff20" : "transparent",
                color: isPocket === o.v ? "#6c63ff" : "#8888a8",
                fontSize: "11px", fontWeight: "600", cursor: "pointer",
              }}>
                {o.t}
              </button>
            ))}
          </div>
        </div>
      )}

      {!isMC && (
        <div style={{ background: "#ffa72610", border: "1px solid #ffa72630", borderRadius: "10px", padding: "10px 14px", fontSize: "12px", color: "#ffa726", marginBottom: "14px" }}>
          💡 This is a reimbursement request for money paid from your pocket. Select the month you spent it in — once approved, the Treasurer will pay you back via UPI.
        </div>
      )}

      {err && (
        <div style={{ background: "#ff525218", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 12px", marginBottom: "12px", color: "#ff5252", fontSize: "13px" }}>
          {err}
        </div>
      )}

      <button onClick={handleSubmit} disabled={saving} style={{ ...S.primaryBtn, padding: "14px", fontSize: "15px", opacity: saving ? 0.7 : 1 }}>
        {saving ? "Saving…" : "Add Expense"}
      </button>
    </Modal>
  );
}


// ════════════════════════════════════════════════════════════════
// NOTE: Tanker Order Modal, Record Tanker Payment Modal, and Batch
// Payment Action Modal have moved to WaterScreen.jsx (inside the
// WaterTankerExpensesTab component below). They are no longer
// defined here to avoid duplicate code.
// ════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════
// GMAIL-SOURCED EXPENSE ACTIONS — moved in from gmailService.js
// (gmailService.js retired; these 2 are the only functions from it
// that were actually still in use — Treasurer/President actions below)
// ════════════════════════════════════════════════════════════════

/**
 * Called when Treasurer resolves an unmatched debit. A contact already
 * exists by this point (created/found automatically during sync), so
 * this function just needs to know:
 *   - which expense bucket it belongs to (Maintenance or Water Tanker)
 *   - the expense category to file it under
 *   - the final phone number/category for the contact (Treasurer may edit)
 *
 * If expenseType is "maintenance": recorded as a normal expense, same as today.
 * If expenseType is "tanker": since the bank already shows this as paid,
 * we record it directly into tankerOrders as a paid, one-off "retroactive"
 * order (no batch, no President approval needed) — and notify the
 * President so he's aware, instead of asking him to approve it.
 *
 * @param {string} unmatchedDocId  — Firestore doc ID in unmatchedDebits
 * @param {object} vendorDetails   — { name, phone, category, upiId, expenseType }
 * @param {string} assignedBy      — name of the Treasurer making the assignment
 */
async function assignUnmatchedDebit(unmatchedDocId, vendorDetails, assignedBy) {
  const unmatchedRef  = doc(db, "unmatchedDebits", unmatchedDocId);
  const unmatchedSnap = await getDoc(unmatchedRef);
  if (!unmatchedSnap.exists()) throw new Error("Unmatched debit not found.");

  const debit = unmatchedSnap.data();
  const cleanPhone = (vendorDetails.phone || "0000000000").trim();
  const cleanUpiId = (vendorDetails.upiId || debit.vendorUpiId || "").trim();
  const vendorName = vendorDetails.name.trim();
  const expenseType = vendorDetails.expenseType || "maintenance"; // "maintenance" | "tanker"

  const nowStr = new Date().toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });

  // ── 1. Update the contact (already created/found during sync) ──
  // When Treasurer picks "tanker", officially stamp the contact as
  // "Water Tanker Supplier" so future payments auto-route correctly.
  const officialCategory = expenseType === "tanker"
    ? "Water Tanker Supplier"
    : (vendorDetails.category || "Other");

  let contactId = debit.contactId || null;
  if (contactId) {
    const contactRef  = doc(db, "contacts", contactId);
    const contactSnap = await getDoc(contactRef);
    if (contactSnap.exists()) {
      const existingUpiIds = contactSnap.data().upiIds || [];
      await updateDoc(contactRef, {
        name:     vendorName,
        category: officialCategory,
        phones:   [cleanPhone],
        phone:    cleanPhone,
        upiIds:   cleanUpiId && !existingUpiIds.includes(cleanUpiId)
          ? [...existingUpiIds, cleanUpiId] : existingUpiIds,
        // Mark auto-created contacts as confirmed once Treasurer reviews them
        autoCreatedConfirmedBy: assignedBy,
        autoCreatedConfirmedAt: serverTimestamp(),
      });
    }
  } else {
    // Safety net — shouldn't normally happen since sync always creates one
    const newContactRef = await addDoc(collection(db, "contacts"), {
      name: vendorName, category: officialCategory,
      phones: [cleanPhone], phone: cleanPhone,
      upiIds: cleanUpiId ? [cleanUpiId] : [], notes: "", email: "",
      addedBy: assignedBy, addedAt: serverTimestamp(), autoCreated: true,
    });
    contactId = newContactRef.id;
  }

  const month = debit.date
    ? monthKeyFromDate(debit.date.toDate ? debit.date.toDate() : new Date(debit.date))
    : currentMonthKey();
  const dateISO = debit.date
    ? (debit.date.toDate ? debit.date.toDate() : new Date(debit.date)).toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);

  if (expenseType === "tanker") {
    // ── 2a. Water Tanker — check for pending order first ─────────
    // If a pending tankerOrder exists for this vendor, mark it paid
    // (normal approval flow, President stays in loop).
    // Only if nothing pending → retroactive entry.
    const pendingOrderSnap = await getDocs(query(
      collection(db, "tankerOrders"),
      where("supplierName", "==", vendorName),
      where("paymentStatus", "==", "pending")
    ));

    if (!pendingOrderSnap.empty) {
      // ── Pending order found — mark it paid ───────────────────
      const pendingOrder = pendingOrderSnap.docs[0];
      await updateDoc(doc(db, "tankerOrders", pendingOrder.id), {
        paymentStatus: "paid",
        paidAt:        serverTimestamp(),
        gmailMsgId:    debit.gmailMsgId,
        paidBy:        assignedBy,
      });
      const expRef = await addDoc(collection(db, "expenses"), {
        description:  `Water tanker — ${vendorName} (assigned by ${assignedBy})`,
        amount:       debit.amount, date: dateISO, month,
        category:     "Water Tanker", isRegular: false, isPocket: false,
        approvalStatus: "auto_approved",
        paidAt:       serverTimestamp(),
        addedBy:      assignedBy, addedAt: serverTimestamp(),
        tankerOrderId: pendingOrder.id, vendorId: contactId,
        gmailMsgId:   debit.gmailMsgId,
        autoRecorded: false, matchMethod: "manual",
        exceptionTrail: {
          type: "matched_pending_order",
          note: `Matched to pending tanker order ${pendingOrder.id} by ${assignedBy}`,
          at:   nowStr,
        },
        status: "confirmed",
      });
      await updateDoc(doc(db, "tankerOrders", pendingOrder.id), { expenseId: expRef.id });

      await addDoc(collection(db, "notifications"), {
        type:    "tanker_order_paid_via_gmail",
        message: `✅ ₹${debit.amount.toLocaleString("en-IN")} paid to ${vendorName} — matched to pending tanker order and marked paid by ${assignedBy}.`,
        date: dateISO, amount: debit.amount, vendorName,
        forRoles: ["President"], read: false, createdAt: serverTimestamp(),
      });
    } else {
      // ── No pending order — retroactive entry ─────────────────
      const tankerOrderRef = await addDoc(collection(db, "tankerOrders"), {
        date: dateISO, month, count: 1, rate: debit.amount, amount: debit.amount,
        remarks: "Retroactive entry — bank debit matched after the fact by Treasurer",
        supplierName: vendorName,
        orderedBy: `${assignedBy} (via Gmail)`, orderedAt: serverTimestamp(),
        paymentStatus: "paid", expenseId: null, retroactive: true,
        gmailMsgId: debit.gmailMsgId,
      });
      const expRef = await addDoc(collection(db, "expenses"), {
        description:  `Water tanker (retroactive) — ${vendorName}`,
        amount:       debit.amount, date: dateISO, month,
        category:     "Water Tanker", isRegular: false, isPocket: false,
        approvalStatus: "auto_approved", approvedBy: null, approvedAt: null,
        paidAt:       serverTimestamp(),
        addedBy:      assignedBy, addedAt: serverTimestamp(), isReimbursed: false,
        tankerOrderId: tankerOrderRef.id, vendorId: contactId,
        gmailMsgId:   debit.gmailMsgId,
        autoRecorded: false, matchMethod: "manual",
        retroactive: true, status: "confirmed",
        exceptionTrail: {
          type: "retroactive_manual",
          note: `No pending order found. Added retroactively by ${assignedBy} on ${nowStr}`,
          at:   nowStr,
        },
      });
      await updateDoc(doc(db, "tankerOrders", tankerOrderRef.id), { expenseId: expRef.id });

      // Chat broadcast — visible to all flat owners
      await addDoc(collection(db, "messages"), {
        flatId:     null,
        senderName: "System",
        senderRole: "system",
        roleIcon:   "⏪",
        text: `⏪ Added Later: ₹${debit.amount.toLocaleString("en-IN")} paid to ${vendorName} on ${dateISO} added directly to Water Tanker records (no prior order was logged). Added by ${assignedBy} on ${nowStr}. President has been notified.`,
        createdAt:  serverTimestamp(),
      });
      await addDoc(collection(db, "notifications"), {
        type: "tanker_retroactive_entry",
        message: `⏪ ₹${debit.amount.toLocaleString("en-IN")} paid to ${vendorName} on ${dateISO} added directly to Water Tanker records — old/unrecorded tanker delivery. Added by ${assignedBy}. No approval needed.`,
        date: dateISO, amount: debit.amount, vendorName,
        gmailMsgId: debit.gmailMsgId,
        forRoles: ["President"],
        read: false, createdAt: serverTimestamp(),
      });
    }
  } else {
    // ── 2b. Maintenance — normal expense ─────────────────────────
    await addDoc(collection(db, "expenses"), {
      category:     officialCategory,
      vendorName,
      vendorId:     contactId,
      amount:       debit.amount,
      date:         dateISO,
      month,
      description:  `Manual: ${vendorName} — assigned by ${assignedBy}`,
      gmailMsgId:   debit.gmailMsgId,
      autoRecorded: false,
      matchMethod:  "manual",
      recordedBy:   assignedBy,
      recordedAt:   serverTimestamp(),
      status:       "confirmed",
      exceptionTrail: null,
    });
  }

  // ── 3. Mark unmatched debit as resolved ───────────────────────
  await updateDoc(unmatchedRef, {
    status:      "resolved",
    resolvedBy:  assignedBy,
    resolvedAt:  serverTimestamp(),
    expenseType,
    contactId,
  });
}

/**
 * Called when President corrects a misclassified Gmail-sourced expense.
 * "moveTo" is the target bucket: "tanker" or "maintenance".
 *
 * - If moving TO tanker: creates a retroactive tankerOrder + updates expense.
 * - If moving TO maintenance: removes tankerOrder link + updates expense.
 * - Posts a building-chat message visible to all flat owners.
 * - Stamps exceptionTrail on the expense record.
 *
 * @param {string} expenseId  — Firestore doc ID in expenses
 * @param {string} moveTo     — "tanker" | "maintenance"
 * @param {string} correctedBy — name of the President making the correction
 */
async function correctExpenseClassification(expenseId, moveTo, correctedBy) {
  const expRef  = doc(db, "expenses", expenseId);
  const expSnap = await getDoc(expRef);
  if (!expSnap.exists()) throw new Error("Expense not found.");

  const expense = expSnap.data();
  const nowStr  = new Date().toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
  const originalCategory = expense.category || "Other";
  const dateISO = expense.date || new Date().toISOString().slice(0, 10);
  const month   = expense.month || currentMonthKey();
  const amount  = expense.amount || 0;
  const vendor  = expense.vendorName || "Unknown Vendor";

  const trail = {
    type:              "corrected",
    originalCategory,
    correctedBy,
    correctedAt:       nowStr,
    note: `Moved from ${originalCategory} to ${moveTo === "tanker" ? "Water Tanker" : "Maintenance"} by ${correctedBy}`,
  };

  if (moveTo === "tanker") {
    // Create a retroactive tankerOrder and link it
    const tankerOrderRef = await addDoc(collection(db, "tankerOrders"), {
      date: dateISO, month, count: 1, rate: amount, amount,
      remarks: `Correction by President — originally recorded as ${originalCategory}`,
      supplierName: vendor,
      orderedBy:   correctedBy, orderedAt: serverTimestamp(),
      paymentStatus: "paid", retroactive: true,
      gmailMsgId:  expense.gmailMsgId || null,
    });
    await updateDoc(expRef, {
      category:       "Water Tanker",
      tankerOrderId:  tankerOrderRef.id,
      corrected:      true,
      exceptionTrail: trail,
      correctedAt:    serverTimestamp(),
    });
    await updateDoc(doc(db, "tankerOrders", tankerOrderRef.id), { expenseId });
  } else {
    // Moving to Maintenance — unlink any tankerOrder
    if (expense.tankerOrderId) {
      await updateDoc(doc(db, "tankerOrders", expense.tankerOrderId), {
        paymentStatus: "voided_by_correction",
        correctedBy, correctedAt: serverTimestamp(),
      });
    }
    await updateDoc(expRef, {
      category:       expense.vendorCategory || "Other",
      tankerOrderId:  null,
      corrected:      true,
      exceptionTrail: trail,
      correctedAt:    serverTimestamp(),
    });
  }

  // Chat broadcast — visible to all flat owners
  await addDoc(collection(db, "messages"), {
    flatId:     null,
    senderName: "System",
    senderRole: "system",
    roleIcon:   "✏️",
    text: `✏️ Corrected: ₹${amount.toLocaleString("en-IN")} paid to ${vendor} on ${dateISO} was wrongly recorded as ${originalCategory} — ${correctedBy} has corrected it to ${moveTo === "tanker" ? "Water Tanker" : "Maintenance"} on ${nowStr}.`,
    createdAt:  serverTimestamp(),
  });

  // Notification to Treasurer
  await addDoc(collection(db, "notifications"), {
    type:    "expense_corrected",
    message: `✏️ President corrected ₹${amount.toLocaleString("en-IN")} paid to ${vendor}: moved from ${originalCategory} to ${moveTo === "tanker" ? "Water Tanker" : "Maintenance"}.`,
    date:    dateISO, amount, vendorName: vendor,
    forRoles: ["Treasurer"],
    read: false, createdAt: serverTimestamp(),
  });
}

// ════════════════════════════════════════════════════════════════
// EXPENSES — Assign Unmatched Debit Modal (Treasurer)
// ════════════════════════════════════════════════════════════════
const EXPENSE_CATEGORIES = [
  "Electricity", "Motor", "Plumber", "Sanitary", "Drainage",
  "Elevator", "Garbage", "Carpenter", "Painter", "Security",
  "Water Tanker Supplier", "Other",
];

function AssignUnmatchedDebitModal({ debit, onClose, onDone }) {
  const { user } = useAuth();
  const [vendorName, setVendorName]     = useState(debit.vendorName || "");
  const [vendorPhone, setVendorPhone]   = useState(debit.vendorMobile || "0000000000");
  const [vendorCategory, setVendorCategory] = useState("Other");
  const [expenseType, setExpenseType]   = useState("maintenance"); // "maintenance" | "tanker"
  const [saving, setSaving]             = useState(false);
  const [err, setErr]                   = useState("");

  async function handleSubmit() {
    if (!vendorName.trim()) { setErr("Vendor name is required."); return; }
    if (!vendorCategory)    { setErr("Please select a category."); return; }
    setSaving(true);
    try {
      await assignUnmatchedDebit(
        debit.id,
        {
          name:        vendorName.trim(),
          phone:       vendorPhone.trim() || "0000000000",
          category:    vendorCategory,
          upiId:       debit.vendorUpiId || "",
          expenseType,
        },
        user.name
      );
      onDone();
    } catch (e) {
      setErr("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Review Unmatched Debit" onClose={onClose}>
      {/* Debit details */}
      <div style={{ ...S.card, border: "1px solid #ffa72640", marginBottom: "16px" }}>
        <div style={S.row}>
          <div>
            <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>
              {debit.vendorName || "Unknown Vendor"}
            </div>
            <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>
              {debit.vendorUpiId || "No UPI ID"} · {fmtDate(debit.date)}
            </div>
          </div>
          <div style={{ fontSize: "18px", fontWeight: "700", color: "#ff5252" }}>
            {fmt(debit.amount)}
          </div>
        </div>
        {debit.contactId && (
          <div style={{ fontSize: "11px", color: "#6c63ff", marginTop: "8px" }}>
            {debit.contactIsNew
              ? "✓ A new vendor was already added to Contacts for this payment."
              : "✓ Matched to an existing vendor already in Contacts."}
          </div>
        )}
      </div>

      {/* Vendor details — pre-filled, editable */}
      <div style={{ marginBottom: "14px" }}>
        <label style={S.label}>Vendor Name</label>
        <input
          type="text" value={vendorName}
          onChange={e => setVendorName(e.target.value)}
          placeholder="Enter vendor name" style={S.input}
        />
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label style={S.label}>Phone</label>
        <input
          type="tel" value={vendorPhone}
          onChange={e => setVendorPhone(e.target.value)}
          placeholder="Mobile number" style={S.input}
        />
        <div style={{ fontSize: "11px", color: "#555570", marginTop: "4px" }}>
          Pre-filled from UPI ID. Update if incorrect.
        </div>
      </div>

      {/* Maintenance vs Water Tanker — this decides where the money goes */}
      <div style={{ marginBottom: "14px" }}>
        <label style={S.label}>This payment is for</label>
        <select value={expenseType} onChange={e => setExpenseType(e.target.value)} style={S.input}>
          <option value="maintenance">🧾 Maintenance Expense</option>
          <option value="tanker">🚛 Water Tanker</option>
        </select>
        <div style={{ fontSize: "11px", color: "#555570", marginTop: "4px" }}>
          {expenseType === "tanker"
            ? "Will be added directly to Water Tanker records as already paid — President will be notified, no approval needed."
            : "Will be recorded as a normal expense."}
        </div>
      </div>

      <div style={{ marginBottom: "20px" }}>
        <label style={S.label}>Category</label>
        <select value={vendorCategory} onChange={e => setVendorCategory(e.target.value)} style={S.input}>
          {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {err && (
        <div style={{ background: "#ff525218", border: "1px solid #ff525240", borderRadius: "10px", padding: "10px 12px", marginBottom: "12px", color: "#ff5252", fontSize: "13px" }}>
          {err}
        </div>
      )}

      <button onClick={handleSubmit} disabled={saving} style={{ ...S.primaryBtn, padding: "14px", fontSize: "15px", opacity: saving ? 0.7 : 1 }}>
        {saving ? "Saving…" : "✅ Confirm & Record"}
      </button>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════
// CORRECT EXPENSE BUTTON — President only, Gmail-sourced expenses
// ════════════════════════════════════════════════════════════════
function CorrectExpenseButton({ expense, onDone, user }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // "tanker" | "maintenance" | null

  const isCurrentlyTanker = (expense.category || "") === "Water Tanker";
  const moveTo = isCurrentlyTanker ? "maintenance" : "tanker";
  const label  = isCurrentlyTanker
    ? "Move to Maintenance"
    : "Move to Water Tanker";

  async function doCorrect() {
    setBusy(true);
    try {
      await correctExpenseClassification(expense.id, moveTo, user.name || "President");
      setConfirm(null);
      onDone();
    } catch (err) {
      alert("Error correcting expense: " + err.message);
    } finally {
      setBusy(false);
    }
  }

  if (confirm) {
    return (
      <div style={{ marginTop: "6px", fontSize: "11px", color: "#fbbf24",
        background: "#1c1c30", borderRadius: "6px", padding: "8px 10px" }}>
        Move ₹{expense.amount?.toLocaleString("en-IN")} from {expense.category} to{" "}
        {moveTo === "tanker" ? "Water Tanker" : "Maintenance"}?
        All flat owners will be notified in the building chat.
        <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
          <button onClick={doCorrect} disabled={busy}
            style={{ fontSize: "11px", padding: "3px 10px",
              background: "#1d4ed8", color: "#fff", border: "none",
              borderRadius: "4px", cursor: "pointer" }}>
            {busy ? "Moving…" : "Yes, Move"}
          </button>
          <button onClick={() => setConfirm(null)}
            style={{ fontSize: "11px", padding: "3px 10px",
              background: "#2a2a44", color: "#aaa", border: "none",
              borderRadius: "4px", cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <button onClick={() => setConfirm(moveTo)}
      style={{ marginTop: "5px", fontSize: "10px", padding: "2px 8px",
        background: "#2a2a44", color: "#93c5fd", border: "1px solid #334",
        borderRadius: "4px", cursor: "pointer" }}>
      ✏️ {label}
    </button>
  );
}

// ════════════════════════════════════════════════════════════════
// EXPENSES SUB-TAB
// ════════════════════════════════════════════════════════════════
function ExpensesTab({ user, isMC, isPresident, isSecretary, isTreasurer, settings, refreshKey, setRefreshKey }) {
  const fyMonths = getFYMonths();
  const [view, setViewRaw] = useState("maintenance"); // maintenance | tanker
  const setView = (v) => setViewRaw(isMC ? v : "maintenance"); // non-MC can never switch to tanker
  const [expenses, setExpenses] = useState([]);
  const [allExpenses, setAllExpenses] = useState([]);
  const [unmatchedDebits, setUnmatchedDebits] = useState([]);
  const [assigningDebit, setAssigningDebit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterMonth, setFilterMonth] = useState(currentMonthKey());
  const [showAddExpense, setShowAddExpense] = useState(false);

  const regularCategories = settings?.regularExpenseCategories || DEFAULT_REGULAR_CATEGORIES;
  const fyStartMonth = getFYMonths()[0]?.key || "2026-04";

  // ── Pocket Expense Approval Limit (President-editable, defaults to 5000) ──
  const [editingLimit, setEditingLimit] = useState(false);
  const [limitInput, setLimitInput] = useState("");
  const [savingLimit, setSavingLimit] = useState(false);
  const approvalLimit = settings?.pocketExpenseApprovalLimit ?? 5000;

  async function saveApprovalLimit() {
    const val = Number(limitInput);
    if (!val || val <= 0) return;
    setSavingLimit(true);
    try {
      await setDoc(doc(db, "settings", "init"), { pocketExpenseApprovalLimit: val }, { merge: true });
      setEditingLimit(false);
      setRefreshKey(k => k + 1);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingLimit(false);
    }
  }

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        // Selected month data — for the list display
        const expSnap = await getDocs(query(collection(db, "expenses"), where("month", "==", filterMonth)));
        const exps = expSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        exps.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
        setExpenses(exps);

        // All FY data up to selected month — for cumulative chip
        const allExpSnap = await getDocs(query(
          collection(db, "expenses"),
          where("month", ">=", fyStartMonth),
          where("month", "<=", filterMonth)
        ));
        setAllExpenses(allExpSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Unmatched debits — pending Treasurer review
        const uDebitSnap = await getDocs(query(
          collection(db, "unmatchedDebits"),
          where("status", "==", "unmatched")
        ));
        setUnmatchedDebits(uDebitSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [filterMonth, refreshKey]);

  async function approveExpense(exp) {
    // Secretary approval — check against the President-editable limit stored in DB
    if ((exp.amount || 0) > approvalLimit) {
      // Above limit — needs President's approval next, not ready to pay yet
      await updateDoc(doc(db, "expenses", exp.id), {
        approvalStatus: "pending_president_approval",
        secretaryApprovedBy: user.name, secretaryApprovedAt: serverTimestamp(),
      });
    } else {
      // At or below limit — Secretary's approval alone is enough
      await updateDoc(doc(db, "expenses", exp.id), {
        approvalStatus: "approved", approvedBy: user.name, approvedAt: serverTimestamp(),
      });
    }
    setRefreshKey(k => k + 1);
  }

  async function presidentApproveExpense(exp) {
    // President's approval — only reached for above-limit expenses, after Secretary
    await updateDoc(doc(db, "expenses", exp.id), {
      approvalStatus: "approved", approvedBy: user.name, approvedAt: serverTimestamp(),
    });
    setRefreshKey(k => k + 1);
  }

  async function markExpensePaid(exp) {
    // FIX (PassbookScreen 3.1 + 3.2, frozen 2026-07-13): the expense status update,
    // balance read+write, and tanker order update used to happen as 3 separate,
    // unprotected steps — risking (a) a lost balance update if something else touched
    // the balance at the same moment, and (b) an expense left marked "Paid" without the
    // balance actually being reduced, if the app failed partway through. Wrapping all
    // three in one Firestore transaction fixes both: the balance read+write is locked
    // against interference, and all three writes now succeed together or not at all.
    const expRef = doc(db, "expenses", exp.id);
    // const settingsRef = doc(db, "settings", "init"); // unused now — see CLEANUP note below
    const tankerRef = exp.tankerOrderId ? doc(db, "tankerOrders", exp.tankerOrderId) : null;

    await runTransaction(db, async (transaction) => {
      // CLEANUP — commented out, not deleted, 13 Jul 2026 (per item #11 follow-up):
      // settings.appBalance is no longer read or shown anywhere — App Balance is now
      // calculated fresh every time by computeLiveAppBalance() at the top of this file.
      // Whole old-balance block (read + write) left here together, commented, in case
      // we ever need to see how the old stored-balance approach worked.
      // const settingsSnap = await transaction.get(settingsRef);
      // const currentBal = settingsSnap.exists() ? (settingsSnap.data().appBalance || 0) : 0;

      transaction.update(expRef, { approvalStatus: "paid", paidAt: serverTimestamp() });
      // transaction.set(settingsRef, { appBalance: currentBal - (exp.amount || 0) }, { merge: true });
      if (tankerRef) {
        transaction.update(tankerRef, { paymentStatus: "paid" });
      }
    });

    // ── Notify flat owner + President that the pocket expense is paid ──
    // Same pattern as NotificationBell.jsx's writeNotification(), reused directly.
    const message = `Your pocket expense of ${fmt(exp.amount)} (${exp.description}) has been paid.`;
    try {
      if (exp.addedByUid) {
        await addDoc(collection(db, "notifications"), {
          toUid: exp.addedByUid, message, type: "pocket_expense_paid",
          isRead: false, createdAt: serverTimestamp(),
        });
      }
      const presSnap = await getDocs(query(collection(db, "users"), where("role", "==", "President")));
      if (!presSnap.empty) {
        const presidentUid = presSnap.docs[0].id;
        await addDoc(collection(db, "notifications"), {
          toUid: presidentUid, message, type: "pocket_expense_paid",
          isRead: false, createdAt: serverTimestamp(),
        });
      }
    } catch (e) {
      console.error("Notification write error:", e);
    }

    setRefreshKey(k => k + 1);
  }

  if (loading) {
    return <div style={{ textAlign: "center", padding: "60px 20px", color: "#555570", fontSize: "13px" }}>Loading expenses…</div>;
  }

  // ── Maintenance view ────────────────────────────────────────
  if (view === "maintenance") {
    const pendingApproval = expenses.filter(e => !e.isRegular && e.approvalStatus === "pending_president" && e.category !== "Water Tanker");
    const pendingPresidentApproval = expenses.filter(e => !e.isRegular && e.approvalStatus === "pending_president_approval" && e.category !== "Water Tanker");
    const readyToPay = expenses.filter(e => !e.isRegular && e.approvalStatus === "approved" && e.category !== "Water Tanker");
    const cumulativeTotal = allExpenses.filter(e => e.category !== "Water Tanker").reduce((s, e) => s + (e.amount || 0), 0);
    const appBalance = settings?.appBalance ?? 0;
    const bankBalance = settings?.bankBalance ?? appBalance; // until Gmail wired, treat as equal
    const surplus = bankBalance - appBalance;

    return (
      <div>
        <Toggle view={view} setView={setView} isMC={isMC} />

        {/* Unmatched debits — Treasurer review banner */}
        {isTreasurer && unmatchedDebits.length > 0 && (
          <>
            <div style={S.sectionTitle}>⚠ Unmatched Debits — Awaiting Review ({unmatchedDebits.length})</div>
            {unmatchedDebits.map(d => (
              <div key={d.id} style={{ ...S.card, border: "1px solid #ff525240" }}>
                <div style={S.row}>
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>
                      {d.vendorName || "Unknown Vendor"}
                    </div>
                    <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>
                      {d.vendorUpiId || "No UPI ID"} · {fmtDate(d.date)}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "16px", fontWeight: "700", color: "#ff5252" }}>{fmt(d.amount)}</div>
                  </div>
                </div>
                <button
                  onClick={() => setAssigningDebit(d)}
                  style={{ ...S.primaryBtn, marginTop: "10px", padding: "9px", fontSize: "12px", background: "#ffa726" }}
                >
                  📋 Review & Record
                </button>
              </div>
            ))}
          </>
        )}
        {isPresident && (
          <div style={{
            ...S.card, border: "1px solid #6c63ff40", marginBottom: "14px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div>
              <div style={{ fontSize: "12px", color: "#8888a8" }}>Pocket Expense Approval Limit</div>
              {editingLimit ? (
                <input
                  type="number"
                  autoFocus
                  value={limitInput}
                  onChange={e => setLimitInput(e.target.value)}
                  style={{
                    marginTop: "4px", width: "120px", background: "#0f0f13",
                    border: "1px solid #2e2e40", borderRadius: "8px",
                    padding: "6px 10px", color: "#e8e8f0", fontSize: "14px", outline: "none",
                  }}
                />
              ) : (
                <div style={{ fontSize: "16px", fontWeight: "700", color: "#6c63ff", marginTop: "2px" }}>{fmt(approvalLimit)}</div>
              )}
              <div style={{ fontSize: "10px", color: "#555570", marginTop: "3px" }}>
                Above this amount, your approval is also needed after the Secretary's.
              </div>
            </div>
            {editingLimit ? (
              <button
                onClick={saveApprovalLimit}
                disabled={savingLimit}
                style={{ ...S.primaryBtn, padding: "8px 14px", fontSize: "12px", background: "#4caf50" }}
              >
                {savingLimit ? "Saving…" : "Save"}
              </button>
            ) : (
              <button
                onClick={() => { setLimitInput(String(approvalLimit)); setEditingLimit(true); }}
                style={{ ...S.ghostBtn, color: "#6c63ff", borderColor: "#6c63ff40" }}
              >
                Edit
              </button>
            )}
          </div>
        )}

        {isSecretary && pendingApproval.length > 0 && (
          <>
            <div style={S.sectionTitle}>🔔 Pending Your Approval</div>
            {pendingApproval.map(e => (
              <div key={e.id} style={{ ...S.card, border: "1px solid #6c63ff40" }}>
                <div style={S.row}>
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>{e.description}</div>
                    <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>{e.category} · {e.addedBy} · {fmtDate(e.date)}</div>
                  </div>
                  <div style={{ fontSize: "16px", fontWeight: "700", color: "#6c63ff" }}>{fmt(e.amount)}</div>
                </div>
                <button onClick={() => approveExpense(e)} style={{ ...S.primaryBtn, marginTop: "10px", padding: "9px", fontSize: "12px", background: "#4caf50" }}>
                  ✅ Approve
                </button>
              </div>
            ))}
          </>
        )}

        {/* President — second approval, only for expenses above the limit */}
        {isPresident && pendingPresidentApproval.length > 0 && (
          <>
            <div style={S.sectionTitle}>🔔 Pending Your Approval (Above Limit)</div>
            {pendingPresidentApproval.map(e => (
              <div key={e.id} style={{ ...S.card, border: "1px solid #ffa72640" }}>
                <div style={S.row}>
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>{e.description}</div>
                    <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>
                      {e.category} · {e.addedBy} · {fmtDate(e.date)} · Secretary-approved by {e.secretaryApprovedBy}
                    </div>
                  </div>
                  <div style={{ fontSize: "16px", fontWeight: "700", color: "#ffa726" }}>{fmt(e.amount)}</div>
                </div>
                <button onClick={() => presidentApproveExpense(e)} style={{ ...S.primaryBtn, marginTop: "10px", padding: "9px", fontSize: "12px", background: "#4caf50" }}>
                  ✅ Approve
                </button>
              </div>
            ))}
          </>
        )}

        {/* Treasurer — ready to pay */}
        {isTreasurer && readyToPay.length > 0 && (
          <>
            <div style={S.sectionTitle}>💵 Ready to Pay</div>
            {readyToPay.map(e => (
              <div key={e.id} style={{ ...S.card, border: "1px solid #4caf5040" }}>
                <div style={S.row}>
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>{e.description}</div>
                    <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>{e.category} · approved by {e.approvedBy}</div>
                  </div>
                  <div style={{ fontSize: "16px", fontWeight: "700", color: "#4caf50" }}>{fmt(e.amount)}</div>
                </div>
                <button onClick={() => markExpensePaid(e)} style={{ ...S.primaryBtn, marginTop: "10px", padding: "9px", fontSize: "12px", background: "#4caf50" }}>
                  💸 Mark as Paid
                </button>
              </div>
            ))}
          </>
        )}

        {/* Month chips + add */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={S.sectionTitle}>Expenses</div>
          <button onClick={() => setShowAddExpense(true)} style={{ ...S.ghostBtn, color: "#6c63ff", borderColor: "#6c63ff40" }}>+ Add</button>
        </div>
        <MonthChips months={fyMonths} active={filterMonth} onSelect={setFilterMonth} />

        {/* Cumulative Expenses chip — 3-column */}
        <div style={{ background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px", padding: "12px 16px", marginBottom: "12px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", textAlign: "center" }}>
            <div style={{ borderRight: "1px solid #2e2e40", paddingRight: "8px" }}>
              <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "2px" }}>Cumulative</div>
              <div style={{ fontSize: "10px", color: "#555570", marginBottom: "6px" }}>
                {allExpenses.filter(e => e.month < filterMonth && e.category !== "Water Tanker").length > 0
                  ? `(${fmtMonth(fyStartMonth)} to ${getFYMonths().filter(m => m.key < filterMonth).slice(-1)[0]?.label || fmtMonth(fyStartMonth)})`
                  : `(${fmtMonth(fyStartMonth)})`}
              </div>
              <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>
                {fmt(allExpenses.filter(e => e.month < filterMonth && e.category !== "Water Tanker").reduce((s, e) => s + (e.amount || 0), 0))}
              </div>
            </div>
            <div style={{ borderRight: "1px solid #2e2e40", paddingRight: "8px" }}>
              <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "2px" }}>This Month</div>
              <div style={{ fontSize: "10px", color: "#555570", marginBottom: "6px" }}>({fmtMonth(filterMonth)})</div>
              <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>{fmt(expenses.filter(e => e.category !== "Water Tanker").reduce((s, e) => s + (e.amount || 0), 0))}</div>
            </div>
            <div>
              <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "2px" }}>Total Expenses</div>
              <div style={{ fontSize: "10px", color: "#555570", marginBottom: "6px" }}>&nbsp;</div>
              <div style={{ fontSize: "14px", fontWeight: "700", color: "#6c63ff" }}>{fmt(cumulativeTotal)}</div>
            </div>
          </div>
        </div>

        {expenses.filter(e => e.category !== "Water Tanker").length === 0 ? (
          <div style={{ textAlign: "center", padding: "30px", color: "#555570", fontSize: "13px" }}>No expenses recorded for this month.</div>
        ) : (
          expenses.filter(e => e.category !== "Water Tanker").map(e => {
            const statusColor = e.approvalStatus === "paid" ? "green" : e.approvalStatus === "approved" ? "blue" : e.approvalStatus === "pending_president_approval" ? "amber" : "amber";
            const statusLabel = e.approvalStatus === "paid" ? "Paid" : e.approvalStatus === "approved" ? "Approved" : e.isRegular ? "Approved" : e.approvalStatus === "pending_president_approval" ? "Pending President" : "Pending Secretary";
            const isMismatch = e.gmailMsgId && e.autoRecorded &&
              (e.vendorCategory || "").toLowerCase().includes("tanker") &&
              (e.category || "") !== "Water Tanker";
            return (
              <div key={e.id} style={{ ...S.card, padding: "10px 14px",
                border: isMismatch ? "1px solid #f59e0b" : undefined }}>
                <div style={S.row}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>
                      {e.description}
                      {isMismatch && (
                        <span style={{ marginLeft: "6px", fontSize: "11px",
                          background: "#78350f", color: "#fbbf24",
                          borderRadius: "4px", padding: "1px 6px" }}>
                          ⚠️ Check Category
                        </span>
                      )}
                      {e.exceptionTrail?.type === "corrected" && (
                        <span style={{ marginLeft: "6px", fontSize: "11px",
                          background: "#1e3a5f", color: "#93c5fd",
                          borderRadius: "4px", padding: "1px 6px" }}>
                          ✏️ Corrected
                        </span>
                      )}
                      {e.retroactive && (
                        <span style={{ marginLeft: "6px", fontSize: "11px",
                          background: "#1a2e1a", color: "#86efac",
                          borderRadius: "4px", padding: "1px 6px" }}>
                          ⏪ Added Later
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: "11px", color: "#8888a8", marginTop: "2px" }}>
                      {e.category} · {fmtDate(e.date)}{e.isPocket ? " · 👤 Pocket" : ""}
                    </div>
                    {e.exceptionTrail?.note && (
                      <div style={{ fontSize: "10px", color: "#6666aa", marginTop: "2px", fontStyle: "italic" }}>
                        {e.exceptionTrail.note}
                      </div>
                    )}
                    {isPresident && e.gmailMsgId && !e.corrected && (
                      <CorrectExpenseButton expense={e} onDone={() => setRefreshKey(k => k + 1)} user={user} />
                    )}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>{fmt(e.amount)}</div>
                    <Badge text={statusLabel} color={statusColor} />
                  </div>
                </div>
              </div>
            );
          })
        )}

        {/* Bank balance / surplus — hidden for now, to be shown in Bank tab */}

        {showAddExpense && (
          <AddExpenseModal user={user} isMC={isMC} regularCategories={regularCategories}
            onClose={() => setShowAddExpense(false)} onDone={() => { setShowAddExpense(false); setRefreshKey(k => k + 1); }} />
        )}

        {assigningDebit && (
          <AssignUnmatchedDebitModal
            debit={assigningDebit}
            onClose={() => setAssigningDebit(null)}
            onDone={() => { setAssigningDebit(null); setRefreshKey(k => k + 1); }}
          />
        )}
      </div>
    );
  }


  // ── Water Tanker view — moved to WaterScreen.jsx ─────────────
  return (
    <div>
      <Toggle view={view} setView={setView} isMC={isMC} />
      <WaterTankerExpensesTab
        user={user}
        isMC={isMC}
        isPresident={isPresident}
        isTreasurer={isTreasurer}
        settings={settings}
        refreshKey={refreshKey}
        setRefreshKey={setRefreshKey}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// RESIDENT PASSBOOK TAB
// Shown to Flat Owners and Tenants instead of Collections/Expenses
// Shows: Maintenance payments + Water payments + Pocket expenses
// Filtered by selected month, default = current month
// ════════════════════════════════════════════════════════════════
function ResidentPassbookTab({ user, settings, refreshKey, setRefreshKey, appBalance = 0, bankBalance = 0, balanceLoading = false, bankSyncActive = false, heldForReversal = 0 }) {
  const { isPresident } = useAuth();
  const [showBank, setShowBank] = useState(false);
  const fyMonths = getFYMonths();
  const [filterMonth, setFilterMonth] = useState(currentMonthKey());
  const [payments,      setPayments]      = useState([]);
  const [waterBills,    setWaterBills]    = useState([]);
  const [maintBills,    setMaintBills]    = useState([]);
  const [expenses,      setExpenses]      = useState([]);
  const [adhocPayments, setAdhocPayments] = useState([]);
  const [adhocEvents,   setAdhocEvents]   = useState({});
  const [disabledEventIds, setDisabledEventIds] = useState(new Set());
  const [loading,       setLoading]       = useState(true);
  const [showAdd,       setShowAdd]       = useState(false);
  const [showArchive,   setShowArchive]   = useState(false);

  // Current FY starts April of the FY start year
  const now = new Date();
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const FY_START_KEY = `${fyStartYear}-04`;

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const pSnap = await getDocs(query(
          collection(db, "payments"),
          where("flatId", "==", user.flatId)
        ));
        setPayments(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const wSnap = await getDocs(query(
          collection(db, "waterBills"),
          where("flatId", "==", user.flatId)
        ));
        setWaterBills(wSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const mSnap = await getDocs(query(
          collection(db, "maintenanceBills"),
          where("flatId", "==", user.flatId)
        ));
        setMaintBills(mSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const eSnap = await getDocs(query(
          collection(db, "expenses"),
          where("addedBy", "==", user.name),
          where("isPocket", "==", true)
        ));
        setExpenses(eSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const aSnap = await getDocs(query(
          collection(db, "adhocPayments"),
          where("flatId", "==", user.flatId)
        ));
        const adhocList = aSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAdhocPayments(adhocList);

        // Fetch each referenced event once — gets both the title (for any
        // record missing eventTitle) and whether it's disabled (needed so
        // an unpaid due from a disabled event can show "No Need to Pay"
        // instead of "Pending" — fix frozen 2026-07-13).
        const allEventIds = [...new Set(adhocList.map(a => a.eventId).filter(Boolean))];
        if (allEventIds.length > 0) {
          const evMap = {};
          const disabledSet = new Set();
          await Promise.all(allEventIds.map(async (eid) => {
            try {
              const evSnap = await getDoc(doc(db, "adhocEvents", eid));
              if (evSnap.exists()) {
                evMap[eid] = evSnap.data().title || "Society Work";
                if (evSnap.data().disabled === true) disabledSet.add(eid);
              }
            } catch {}
          }));
          setAdhocEvents(evMap);
          setDisabledEventIds(disabledSet);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [user.flatId, user.name, refreshKey]);

  if (loading) {
    return <div style={{ textAlign: "center", padding: "60px 20px", color: "#555570", fontSize: "13px" }}>Loading passbook…</div>;
  }

  const monthPayment  = payments.find(p => p.month === filterMonth && p.type === "Maintenance");
  const monthBill     = maintBills.find(b => b.month === filterMonth);
  const monthWater    = waterBills.find(w => w.month === filterMonth);
  const monthExpenses = expenses.filter(e => e.month === filterMonth);

  // Helper: get the correct month key for an adhocPayment
  // Priority: paidDate first (real payment date), then addedAt (creation date)
  function getAdhocMonth(a) {
    if (a.paidDate && a.paidDate.length >= 7) return a.paidDate.slice(0, 7);
    if (a.addedAt) {
      const d = a.addedAt.toDate ? a.addedAt.toDate() : new Date(a.addedAt.seconds * 1000);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    }
    return null;
  }

  // Helper: get display title for an adhocPayment
  function getAdhocTitle(a) {
    return a.eventTitle || adhocEvents[a.eventId] || "Society Work";
  }

  // Split adhocPayments: current FY vs previous FY (archive)
  const currentFYAdhoc = adhocPayments.filter(a => {
    const m = getAdhocMonth(a);
    return m && m >= FY_START_KEY;
  });
  const archiveAdhoc = adhocPayments.filter(a => {
    const m = getAdhocMonth(a);
    return !m || m < FY_START_KEY;
  });

  // Build combined entries for the selected month ONLY.
  // Each block below produces at most ONE entry, for the currently selected
  // month only — using a fixed, predictable ID so there's no chance of two
  // different records accidentally colliding or lingering on screen.
  const entries = [];

  if (monthBill || monthPayment) {
    entries.push({
      id: `maintenance-${filterMonth}`,
      type: "Maintenance",
      icon: "🏠",
      month: filterMonth,
      amount: monthBill?.amountDue ?? monthBill?.balance ?? monthBill?.rate ?? monthPayment?.amount ?? 0,
      status: monthPayment ? monthPayment.status : (monthBill?.paid ? "confirmed" : "pending"),
      date: monthPayment?.paidAt || monthPayment?.confirmedAt || monthBill?.paidDate || null,
      note: monthPayment?.autoConfirmed ? "Gmail auto-confirmed" : "",
    });
  }

  if (monthWater) {
    entries.push({
      id: `water-${filterMonth}`,
      type: "Water",
      icon: "💧",
      month: filterMonth,
      monthLabel: `${fmtMonth(prevMonthKey(filterMonth))} usage bill`,
      amount: monthWater.billAmount || monthWater.totalDue || 0,
      status: monthWater.paid ? "confirmed" : "pending",
      date: monthWater.paidDate || null,
      note: "",
    });
  }

  monthExpenses.forEach(e => {
    entries.push({
      id: e.id,
      type: "Pocket Expense",
      icon: "🧾",
      month: e.month,
      amount: e.amount,
      status: e.approvalStatus === "paid" ? "confirmed"
            : e.approvalStatus === "approved" ? "approved"
            : e.approvalStatus === "pending_president_approval" ? "pending"
            : e.approvalStatus === "pending_president" ? "pending"
            : "rejected",
      // Specific to Pocket Expense only — shows the owner exactly which
      // of the (up to) 4 stages their request is at. Does not affect Water
      // or Society Work entries, which use the generic statusLabel() below.
      customStatusLabel: e.approvalStatus === "paid" ? "Paid"
            : e.approvalStatus === "approved" ? "Approved — Ready for Payment"
            : e.approvalStatus === "pending_president_approval" ? "Secretary approved — pending President approval"
            : e.approvalStatus === "pending_president" ? "Pending Secretary"
            : "Rejected",
      date: e.paidAt || null,
      note: e.description || "",
    });
  });

  // Society Work — current FY only, bucketed by real paidDate
  // Item 4, frozen 2026-07-13: a refund record (created when an event is
  // disabled after this flat already paid) is shown as its own separate
  // entry — "Refund Pending" until the Treasurer sends it, then "Refunded".
  // The original "Paid" entry for that event is untouched and still shows
  // in the month it was paid, exactly as before.
  currentFYAdhoc.forEach(a => {
    const isRefund = !!a.reversalStatus;
    const eventMonth = isRefund
      ? (a.reversalDate ? a.reversalDate.slice(0, 7) : getAdhocMonth(a))
      : getAdhocMonth(a);
    if (eventMonth === filterMonth) {
      if (isRefund) {
        entries.push({
          id: a.id,
          type: `${getAdhocTitle(a)} — Refund`,
          icon: "↩️",
          month: eventMonth,
          amount: a.amount,
          status: a.reversalStatus === "approved" ? "confirmed" : "pending",
          date: a.reversalDate || null,
          note: a.reversalStatus === "approved" ? "Refunded" : "Event disabled — refund pending",
          customStatusLabel: a.reversalStatus === "approved" ? "Refunded" : "Refund Pending",
        });
      } else {
        const isDisabledUnpaid = !a.paid && disabledEventIds.has(a.eventId);
        entries.push({
          id: a.id,
          type: getAdhocTitle(a),
          icon: isDisabledUnpaid ? "🚫" : "🏗️",
          month: eventMonth,
          amount: a.amount,
          status: a.paid ? "confirmed" : isDisabledUnpaid ? "disabled" : "pending",
          date: a.paidDate || null,
          note: a.paid ? "Paid" : isDisabledUnpaid ? "Event disabled" : "Due — Society Work",
        });
      }
    }
  });

  function statusColor(s) {
    if (s === "confirmed") return "green";
    if (s === "approved")  return "amber";
    if (s === "pending")   return "amber";
    if (s === "rejected")  return "red";
    if (s === "disabled")  return "grey";
    return "amber";
  }

  function statusLabel(s) {
    if (s === "confirmed") return "Paid";
    if (s === "approved")  return "Approved";
    if (s === "pending")   return "Pending";
    if (s === "rejected")  return "Rejected";
    if (s === "disabled")  return "No Need to Pay";
    return "Pending";
  }

  return (
    <div style={{ padding: "0 0 24px" }}>
      {/* Header */}
      <div style={{ marginBottom: "14px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px" }}>
        <div>
          <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>📒 My Passbook</div>
          <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>
            Flat {user.flatId} · Financial Year Apr-{new Date().getFullYear()} onwards
          </div>
        </div>
        <button
          onClick={() => setShowBank(true)}
          style={{
            background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "10px",
            padding: "8px 12px", color: "#42a5f5", fontSize: "12px", fontWeight: "600",
            cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
          }}
        >
          🏦 Bank Details
        </button>
      </div>

      {showBank && (
        <Modal title="🏦 Bank & UPI Details" onClose={() => setShowBank(false)}>
          <BankScreen isPresident={isPresident()} />
        </Modal>
      )}

      {/* Bank Balance / App Balance — Owner logins only, not Tenants.
          Family members share the owner's/tenant's own login, so this
          same memberType check covers them too (design frozen 2026-07-13). */}
      {user.memberType === "owner" && (
        <BalanceWidget appBalance={appBalance} bankBalance={bankBalance} loading={balanceLoading} bankSyncActive={bankSyncActive} />
      )}

      {/* Month selector */}
      <MonthChips months={fyMonths} active={filterMonth} onSelect={setFilterMonth} />

      {/* Entries for selected month */}
      {entries.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "40px 20px",
          color: "#555570", fontSize: "13px",
          background: "#1a1a24", borderRadius: "12px",
          border: "1px solid #2e2e40", marginTop: "8px",
        }}>
          No transactions for {fmtMonth(filterMonth)}.
        </div>
      ) : (
        <div style={{ marginTop: "8px" }}>
          {entries.map(e => (
            <div key={e.id} style={{
              background: "#1a1a24", border: "1px solid #2e2e40",
              borderRadius: "12px", padding: "12px 14px",
              marginBottom: "8px",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <div style={{ fontSize: "20px" }}>{e.icon}</div>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>
                    {e.type}
                  </div>
                  <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>
                    {e.monthLabel || fmtMonth(e.month)}{e.note ? ` · ${e.note}` : ""}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>
                  {fmt(e.amount)}
                </div>
                <Badge text={e.customStatusLabel || statusLabel(e.status)} color={statusColor(e.status)} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add pocket expense button — past and current months only, not future */}
      {filterMonth <= currentMonthKey() && (
        <div style={{ marginTop: "16px" }}>
          <button
            onClick={() => setShowAdd(true)}
            style={{
              width: "100%", padding: "12px", borderRadius: "12px",
              background: "#1a1a24", border: "1px dashed #2e2e40",
              color: "#6c63ff", fontSize: "13px", fontWeight: "600",
              cursor: "pointer",
            }}
          >
            + Submit Pocket Expense
          </button>
          <div style={{ fontSize: "11px", color: "#555570", textAlign: "center", marginTop: "6px" }}>
            Paid something from your pocket for the society? Submit for MC approval.
          </div>
        </div>
      )}

      {/* Archive — previous FY Society Work entries */}
      {archiveAdhoc.length > 0 && (
        <div style={{ marginTop: "20px" }}>
          <div style={{ textAlign: "center" }}>
            <button
              onClick={() => setShowArchive(v => !v)}
              style={{ background: "none", border: "none", color: "#6c63ff", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}
            >
              {showArchive ? "▲ Hide Archive" : `▼ Archive (${archiveAdhoc.length} previous year entries)`}
            </button>
          </div>
          {showArchive && (
            <div style={{ marginTop: "10px", opacity: 0.8 }}>
              <div style={{ fontSize: "11px", color: "#8888a8", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "600", marginBottom: "8px" }}>
                Society Work — Previous Years
              </div>
              {archiveAdhoc.map(a => {
                const m = getAdhocMonth(a);
                // Derive FY label from the month — e.g. "2025-09" → "FY 2025-26"
                let fyLabel = "";
                if (m) {
                  const [y, mo] = m.split("-").map(Number);
                  const fyYear = mo >= 4 ? y : y - 1;
                  fyLabel = `FY ${fyYear}-${String(fyYear + 1).slice(2)}`;
                }
                // Format paidDate properly — convert "2025-09-27" to "27-Sep-25"
                let paidDateDisplay = "";
                if (a.paidDate && a.paidDate.length >= 10) {
                  const pd = new Date(a.paidDate);
                  if (!isNaN(pd)) {
                    paidDateDisplay = pd.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
                  }
                }
                return (
                  <div key={a.id} style={{
                    background: "#1a1a24", border: "1px solid #2e2e40",
                    borderRadius: "12px", padding: "12px 14px", marginBottom: "8px",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                      <div style={{ fontSize: "20px" }}>{a.reversalStatus ? "↩️" : (!a.paid && disabledEventIds.has(a.eventId)) ? "🚫" : "🏗️"}</div>
                      <div>
                        <div style={{ fontSize: "13px", fontWeight: "600", color: "#e8e8f0" }}>
                          {a.reversalStatus ? `${getAdhocTitle(a)} — Refund` : getAdhocTitle(a)}
                        </div>
                        <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>
                          {fyLabel}{paidDateDisplay ? ` · Paid ${paidDateDisplay}` : ""}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: "14px", fontWeight: "700", color: "#e8e8f0" }}>
                        {fmt(a.amount)}
                      </div>
                      {a.reversalStatus ? (
                        <Badge text={a.reversalStatus === "approved" ? "Refunded" : "Refund Pending"} color={a.reversalStatus === "approved" ? "green" : "amber"} />
                      ) : (!a.paid && disabledEventIds.has(a.eventId)) ? (
                        <Badge text="No Need to Pay" color="grey" />
                      ) : (
                        <Badge text={a.paid ? "Paid" : "Due"} color={a.paid ? "green" : "red"} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showAdd && (
        <AddExpenseModal
          user={user}
          isMC={false}
          regularCategories={[]}
          onClose={() => setShowAdd(false)}
          onDone={() => { setShowAdd(false); setRefreshKey(k => k + 1); }}
        />
      )}
    </div>
  );
}

function Toggle({ view, setView, isMC }) {
  const options = isMC
    ? [{ k: "maintenance", t: "🧾 Maintenance" }, { k: "tanker", t: "🚛 Water Tanker" }]
    : [{ k: "maintenance", t: "🧾 Maintenance" }];
  return (
    <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
      {options.map(o => (
        <button key={o.k} onClick={() => setView(o.k)} style={{
          flex: 1, padding: "10px", borderRadius: "10px", border: "1px solid",
          borderColor: view === o.k ? "#6c63ff" : "#2e2e40",
          background: view === o.k ? "#6c63ff20" : "transparent",
          color: view === o.k ? "#6c63ff" : "#8888a8",
          fontSize: "13px", fontWeight: "600", cursor: "pointer",
        }}>
          {o.t}
        </button>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// BANK BALANCE / APP BALANCE — shared widget
// Shown to: FlatOwner, President, Secretary, Treasurer.
// Hidden from: Tenants. (Family members share the owner's or tenant's
// own login, so this same check covers them too — see visibility
// note frozen 2026-07-13.)
// ════════════════════════════════════════════════════════════════
function BalanceWidget({ appBalance, bankBalance, loading, bankSyncActive, heldForReversal = 0 }) {
  const mismatch = !loading && bankSyncActive && Math.round(appBalance) !== Math.round(bankBalance);
  return (
    <div style={{
      background: "#1a1a24", border: `1px solid ${mismatch ? "#ff525260" : "#2e2e40"}`,
      borderRadius: "12px", padding: "12px 16px", marginBottom: "14px",
    }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", textAlign: "center" }}>
        <div style={{ borderRight: "1px solid #2e2e40", paddingRight: "8px" }}>
          <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "4px" }}>🏦 Bank Balance</div>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#42a5f5" }}>
            {loading ? "…" : fmt(bankBalance)}
          </div>
          {!loading && !bankSyncActive && (
            <div style={{ fontSize: "9px", color: "#555570", marginTop: "3px" }}>Bank sync not wired yet</div>
          )}
        </div>
        <div>
          <div style={{ fontSize: "11px", color: "#8888a8", marginBottom: "4px" }}>📒 App Balance</div>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#4caf50" }}>
            {loading ? "…" : fmt(appBalance)}
          </div>
        </div>
      </div>
      {mismatch && (
        <div style={{ fontSize: "10px", color: "#ff5252", marginTop: "8px", textAlign: "center" }}>
          ⚠ Bank and App balances don't match — please review.
        </div>
      )}
      {/* Item 2 — Held for Reversal: informational only, does not change App Balance */}
      {!loading && heldForReversal > 0 && (
        <div style={{
          fontSize: "11px", color: "#ffa726", marginTop: "8px", textAlign: "center",
          background: "#ffa72612", border: "1px solid #ffa72630", borderRadius: "8px", padding: "6px 8px",
        }}>
          ↩ {fmt(heldForReversal)} held for reversal — refunds pending to flats
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MAIN — PassbookScreen
// ════════════════════════════════════════════════════════════════
export default function PassbookScreen() {
  const { user, isMC, isPresident, isSecretary, isTreasurer } = useAuth();
  const [subTab, setSubTab] = useState("collections"); // collections | expenses
  const [settings, setSettings] = useState(null);
  const [flats, setFlats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [appBalance, setAppBalance] = useState(0);
  const [bankBalance, setBankBalance] = useState(0);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [bankSyncActive, setBankSyncActive] = useState(false);
  const [heldForReversal, setHeldForReversal] = useState(0);

  useEffect(() => {
    async function load() {
      try {
        const settingsSnap = await getDoc(doc(db, "settings", "init"));
        if (settingsSnap.exists()) setSettings(settingsSnap.data());

        const flatsSnap = await getDocs(collection(db, "flats"));
        const flatsData = flatsSnap.docs.map(d => ({ flatId: d.id, ...d.data() }));
        const order = ["G1","G2","G3","101","102","103","201","202","203","301","302","303"];
        flatsData.sort((a, b) => order.indexOf(a.flatId) - order.indexOf(b.flatId));
        setFlats(flatsData);

        // Bank Balance / App Balance — design frozen 2026-07-13, updated with
        // opening balance 2026-07-13. App Balance now starts from the real
        // pre-app balance (settings.maintOpeningBalance) instead of zero.
        // Bank Balance shows a plain 0 (not a fake copy of App Balance) until
        // bank-sync.js is fully wired to write a real settings.bankBalance.
        setBalanceLoading(true);
        const openingBal = settingsSnap.exists() ? Number(settingsSnap.data().maintOpeningBalance || 0) : 0;
        const liveAppBalance = await computeLiveAppBalance(openingBal);
        setAppBalance(liveAppBalance);
        const heldTotal = await computeHeldForReversal();
        setHeldForReversal(heldTotal);
        const bankSyncWired = settingsSnap.exists() && settingsSnap.data().bankBalance != null;
        setBankSyncActive(bankSyncWired);
        setBankBalance(bankSyncWired ? settingsSnap.data().bankBalance : 0);
        setBalanceLoading(false);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [refreshKey]);

  if (loading) {
    return <div style={{ textAlign: "center", padding: "60px 20px", color: "#555570", fontSize: "13px" }}>Loading passbook…</div>;
  }

  // Non-MC (Flat Owner / Tenant) — show personal passbook
  if (!isMC()) {
    return (
      <div style={{ padding: "16px", maxWidth: "640px", margin: "0 auto" }}>
        <ResidentPassbookTab
          user={user}
          settings={settings}
          refreshKey={refreshKey}
          setRefreshKey={setRefreshKey}
          appBalance={appBalance}
          bankBalance={bankBalance}
          balanceLoading={balanceLoading}
          bankSyncActive={bankSyncActive}
          heldForReversal={heldForReversal}
        />
      </div>
    );
  }

  // MC view — Collections + Expenses + My Passbook
  return (
    <div>
      {/* Bank Balance / App Balance — shown to all MC roles */}
      <BalanceWidget appBalance={appBalance} bankBalance={bankBalance} loading={balanceLoading} bankSyncActive={bankSyncActive} heldForReversal={heldForReversal} />

      {/* Sub-tab switcher */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        {[
          { k: "collections", t: "💰 Income" },
          { k: "expenses",    t: "📤 Expenses" },
          { k: "mypassbook",  t: "📒 My Passbook" },
        ].map(o => (
          <button key={o.k} onClick={() => setSubTab(o.k)} style={{
            flex: 1, padding: "11px", borderRadius: "12px", border: "1px solid",
            borderColor: subTab === o.k ? "#6c63ff" : "#2e2e40",
            background: subTab === o.k ? "#6c63ff20" : "#1a1a24",
            color: subTab === o.k ? "#6c63ff" : "#8888a8",
            fontSize: "13px", fontWeight: "700", cursor: "pointer",
          }}>
            {o.t}
          </button>
        ))}
      </div>

      {subTab === "collections" ? (
        <CollectionsTab user={user} isMC={isMC()} isPresident={isPresident()} isTreasurer={isTreasurer()} settings={settings} flats={flats} refreshKey={refreshKey} setRefreshKey={setRefreshKey} />
      ) : subTab === "expenses" ? (
        <ExpensesTab user={user} isMC={isMC()} isPresident={isPresident()} isSecretary={isSecretary()} isTreasurer={isTreasurer()} settings={settings} refreshKey={refreshKey} setRefreshKey={setRefreshKey} />
      ) : (
        <ResidentPassbookTab user={user} settings={settings} refreshKey={refreshKey} setRefreshKey={setRefreshKey} />
      )}
    </div>
  );
}
